import {
  ClinicBasicInfo,
  ClinicInfo,
  OrganizationSummary,
  RecordsRequestSubmissionMethod,
  CreatePatientPayload,
  EligibilityCheckPayload,
  EligibilityInfo,
  ExtractAndCreateData,
  ExtractAndCreatePayload,
  FaxDetailResponse,
  FaxListItem,
  AdminFaxListItem,
  AdminInboundCallListItem,
  UpdateInboundCallPayload,
  InboxFeedItem,
  LinkableRecord,
  InvitationResponseDto,
  LoginCredentials,
  LoginResponse,
  NpiCheckInfo,
  NpiCheckPayload,
  PaginatedResponse,
  PatientDetailResponse,
  PatientListItem,
  PendingClinicInvitationResponseDto,
  PhoneCallListItem,
  PhoneCallsQuery,
  ReferralDetailResponse,
  ReferralDocumentsResponse,
  AdminReferralListResponse,
  ReferralListResponse,
  ReferralMessage,
  ReferralMessagesResponse,
  WorkflowMessage,
  WorkflowMessagesResponse,
  WorkflowNotesResponse,
  ReferralResponse,
  RegisterFromInvitationPayload,
  RegisterResponse,
  RegisterUserPayload,
  RetriggerPhoneCallPayload,
  RetriggerPhoneCallResponse,
  SendFaxPayload,
  SendFaxResponse,
  UpdatePatientPayload,
  UploadRequestPayload,
  UploadUrlResponse,
  User,
  UserListItemDto,
  VerifyInvitationResponseDto,
  AdminAnonymizePatientResponse,
  AdminPatientProfile,
  AdminPatientSearchResult,
  AppointmentDetailResponse,
  AppointmentListItem,
  ViewUrlResponse,
  WorkflowNote,
  WorkflowType,
  CreditsResponse,
  SavePaymentMethodPayload,
  ClinicJoinRequestView,
  IncomingJoinRequest,
  JoinRequestDispositionChoice,
} from '@/types';
import { saveLoginResume } from './loginResume';
import { safeLocalStorage } from './safeStorage';
import type { FollowUpOutcome, ReferralStatus } from '@/types/referrals';
import {
  CompleteMrrOnboardingPayload,
  CompleteProviderOnboardingPayload,
  DirectoryMatch,
  MrrOnboardingResponse,
  ProviderOnboardingResponse,
  ProviderSearchResult,
} from '@/types/providers';
import {
  NotificationCounts,
  NotificationPage,
  SubjectOpenCount,
  SubjectType,
} from '@/types/notifications';
import {
  AutomatedEmail,
  AutomatedEmailContent,
  EmailMessage,
  EmailThreadDetail,
  EmailThreadSummary,
  EmailUnreadCount,
  SendEmailRequest,
} from '@/types/emails';
import { ReferralFormValues } from './schemas/referral.schema';
import { triggerBlobDownload } from './download.utils';
import { stripQueryString } from './url';
import type { BookingPayload } from './booking/buildBookingPayload';
import type { InjuryLeadPayload } from './booking/injuryLead';
import { TURNSTILE_TOKEN_HEADER } from './booking/turnstile';
import type { CashPrice } from '@/types/booking';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const DEFAULT_TIMEOUT_MS = 15_000;
/** Longer than the API's 30 s booking transaction when it includes a Stripe charge. */
const CASH_CHARGE_BOOKING_TIMEOUT_MS = 45_000;

// Referral creation renders the facesheet PDF synchronously (in the isolated
// render Lambda), which can cold-start ~20s. Give those specific calls more room
// so a slow-but-successful create doesn't surface a false "unavailable".
const RENDER_CREATE_TIMEOUT_MS = 60_000;

// Admin patient deletion calls WordPress synchronously (Stripe cancel, file
// purge, user delete — up to 30s per email address on record) before
// enqueueing the RMP scrub. A default-timeout abort would tell the admin it
// failed while the server keeps going, inviting a duplicate retry.
const ADMIN_ANONYMIZE_TIMEOUT_MS = 120_000;

// The API reads whole email attachments (up to 25 MB each) from Gmail before
// it answers.
const EMAIL_ATTACHMENT_TIMEOUT_MS = 60_000;

// Shown when a request times out because the server is unreachable/too slow,
// instead of the raw DOMException message "signal is aborted without reason".
export const SERVER_UNAVAILABLE_MESSAGE =
  'The server is temporarily unavailable. Please try again.';

// A session refresh that failed for a reason other than a rejected token.
export const REFRESH_UNAVAILABLE_MESSAGE =
  'Could not renew your session. Please sign in again.';

// Custom, non-CORS-safelisted header the API's global CsrfGuard asserts on every
// cookie-authenticated state-changing request. A cross-site page cannot attach
// it (setting a custom header forces a CORS preflight our origin allowlist
// rejects), so its presence proves the request came from this first-party SPA.
// Attached to every same-origin API request below; NEVER to cross-origin S3
// presigned uploads, where a custom header would break their CORS.
export const CSRF_HEADER_NAME = 'X-CSRF';
export const CSRF_HEADER_VALUE = '1';

/**
 * Error thrown by the HTTP layer (`fetchWithAuth` / `fetchWithoutAuth`) for every
 * failed request. Carries the transport identity so observability — notably the
 * app-wide `MutationCache.onError` in App.tsx — can tag failures by endpoint and
 * status without per-call-site code. `endpoint` is query-stripped (PHI), and
 * `status` is `null` when the request never reached the server (network error /
 * timeout), which is exactly the otherwise-invisible class we want to surface.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly endpoint: string | undefined,
    readonly method: string,
    readonly status: number | null
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The session ran out and could not be refreshed, so the client sent the user
 * to /login (or was already there). Expected, not a defect — `MutationCache.onError`
 * in App.tsx skips it. A 401 that persists *after* a refresh the server accepted
 * is a different, anomalous class and stays a plain `ApiError`.
 */
export class SessionExpiredError extends ApiError {
  constructor(endpoint: string | undefined, method: string) {
    super('Session expired', endpoint, method, 401);
    this.name = 'SessionExpiredError';
  }
}

// Handle session expiration by redirecting to login
function handleSessionExpired() {
  // Don't redirect if already on login page (prevents infinite loop)
  if (window.location.pathname === '/login') {
    return;
  }

  // Clear local storage auth data
  safeLocalStorage.removeItem('user');
  safeLocalStorage.removeItem('activeClinicId');

  saveLoginResume();

  window.location.href = '/login';
}

export interface FetchWithAuthOptions extends RequestInit {
  // Skip redirect to login on 401 - used for auth-checking endpoints
  // like getCurrentUser that determine if user is logged in
  skipRedirectOn401?: boolean;
  /**
   * Runs instead of the staff redirect to /login when the session cannot be
   * refreshed. Patient calls (lib/patientPortalApi.ts) keep the silent refresh
   * but send the patient to their own sign-in page.
   */
  onSessionExpired?: (reason: SessionExpiryReason) => void;
  // Override the default request timeout (30s)
  timeoutMs?: number;
  /** Reads a successful response's body; JSON by default. */
  readBody?: (response: Response) => Promise<unknown>;
}

/** The API's `message`, else the raw body, else the status. */
async function readErrorMessage(response: Response): Promise<string> {
  const errorText = await response.text();
  try {
    return JSON.parse(errorText).message || `Error: ${response.status}`;
  } catch {
    return errorText || `Error: ${response.status}`;
  }
}

/** The body as JSON, or {} when it is empty or not JSON. */
async function readJsonBody(response: Response): Promise<unknown> {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

/**
 * `rejected` (the refresh token is gone or invalid) is the benign, expected
 * outcome. `unavailable` — the endpoint 5xx'd or was unreachable — logs the user
 * out just as hard but is an outage, so callers keep it reportable.
 */
export type RefreshOutcome = 'refreshed' | 'rejected' | 'unavailable';

/** Why a request could not continue: the session is gone, or refresh is down. */
export type SessionExpiryReason = 'rejected' | 'unavailable';

// Ensures only one refresh request is in-flight at a time.
// Concurrent 401s all wait for the same refresh promise.
let refreshPromise: Promise<RefreshOutcome> | null = null;

export async function attemptTokenRefresh(): Promise<RefreshOutcome> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
    },
  })
    .then(res =>
      res.ok ? 'refreshed' : res.status === 401 ? 'rejected' : 'unavailable'
    )
    .catch((): RefreshOutcome => 'unavailable')
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// Common fetch utility with credentials support
export async function fetchWithAuth<T>(
  url: string,
  options: FetchWithAuthOptions = {}
): Promise<T> {
  const {
    skipRedirectOn401,
    onSessionExpired = handleSessionExpired,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    readBody = readJsonBody,
    ...fetchOptions
  } = options;

  // Transport identity attached to every ApiError thrown below, so failures are
  // identifiable in Sentry by endpoint/method without per-call-site code.
  const method = (fetchOptions.method ?? 'GET').toUpperCase();
  const endpoint = stripQueryString(url);

  const makeRequest = async () => {
    const controller = new AbortController();
    // Track whether OUR timeout aborted the request, so we can distinguish a
    // server-unavailable timeout from a caller-initiated cancellation.
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    // Caller-provided signal takes precedence; otherwise use the timeout signal
    const signal = fetchOptions.signal ?? controller.signal;
    try {
      return await fetch(url, {
        ...fetchOptions,
        signal,
        credentials: 'include',
        headers: {
          ...fetchOptions.headers,
          'Content-Type': 'application/json',
          [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
        },
      });
    } catch (err) {
      // The request never reached the server (network error / our timeout).
      // status === null marks this otherwise-invisible class for observability.
      const message =
        timedOut && err instanceof DOMException && err.name === 'AbortError'
          ? SERVER_UNAVAILABLE_MESSAGE
          : err instanceof Error
            ? err.message
            : String(err);
      throw new ApiError(message, endpoint, method, null);
    } finally {
      clearTimeout(timeout);
    }
  };

  const response = await makeRequest();

  if (!response.ok) {
    if (response.status === 401) {
      // Auth checks and sign-ins throw without a refresh attempt, with the
      // API's own words: a sign-in's 401 is "Invalid or expired code".
      if (skipRedirectOn401) {
        throw new ApiError(
          await readErrorMessage(response),
          endpoint,
          method,
          401
        );
      }

      // Attempt silent refresh, then retry the original request
      const refresh = await attemptTokenRefresh();
      if (refresh === 'refreshed') {
        const retryResponse = await makeRequest();
        if (retryResponse.ok) {
          return (await readBody(retryResponse)) as T;
        }
        if (retryResponse.status === 401) {
          // The server accepted the refresh and still rejects the retry — a
          // broken refresh (dropped Set-Cookie, clock skew), not a plain expiry.
          // Stays an ApiError so Sentry keeps seeing it.
          onSessionExpired('rejected');
          throw new ApiError('Session expired', endpoint, method, 401);
        }
        throw new ApiError(
          await readErrorMessage(retryResponse),
          endpoint,
          method,
          retryResponse.status
        );
      }

      onSessionExpired(refresh);
      throw refresh === 'rejected'
        ? new SessionExpiredError(endpoint, method)
        : // The refresh endpoint is down, not the session — an outage that logs
          // everyone out, so it stays reportable.
          new ApiError(REFRESH_UNAVAILABLE_MESSAGE, endpoint, method, 401);
    }

    throw new ApiError(
      await readErrorMessage(response),
      endpoint,
      method,
      response.status
    );
  }

  return (await readBody(response)) as T;
}

// Common fetch utility without authentication
async function fetchWithoutAuth<T>(
  url: string,
  options: RequestInit & { timeoutMs?: number } = {}
): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
  const method = (fetchOptions.method ?? 'GET').toUpperCase();
  const endpoint = stripQueryString(url);
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  // A caller's signal cancels the request without switching the timeout off.
  const callerSignal = fetchOptions.signal;
  if (callerSignal?.aborted) controller.abort();
  else
    callerSignal?.addEventListener('abort', () => controller.abort(), {
      once: true,
    });
  const signal = controller.signal;

  let response: Response;
  try {
    response = await fetch(url, {
      ...fetchOptions,
      signal,
      headers: {
        ...fetchOptions.headers,
        'Content-Type': 'application/json',
        [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
      },
    });
  } catch (err) {
    // Never reached the server — status null marks the class (see ApiError).
    const message =
      timedOut && err instanceof DOMException && err.name === 'AbortError'
        ? SERVER_UNAVAILABLE_MESSAGE
        : err instanceof Error
          ? err.message
          : String(err);
    throw new ApiError(message, endpoint, method, null);
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || `Error: ${response.status}`;
    } catch {
      errorMessage = errorText || `Error: ${response.status}`;
    }
    throw new ApiError(errorMessage, endpoint, method, response.status);
  }

  const text = await response.text();
  try {
    return (text ? JSON.parse(text) : {}) as T;
  } catch {
    return {} as T;
  }
}

export async function login(
  credentials: LoginCredentials
): Promise<LoginResponse> {
  return fetchWithoutAuth(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(credentials),
  });
}

/**
 * Whether an account already exists for this email. Used by the onboarding
 * wizards at the contact-info step to offer sign-in (and an auto-send) to a
 * returning user instead of routing them through password creation.
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  const { exists } = await fetchWithoutAuth<{ exists: boolean }>(
    `${API_BASE_URL}/auth/email-exists`,
    {
      method: 'POST',
      body: JSON.stringify({ email }),
    }
  );
  return exists;
}

export async function register(
  payload: RegisterUserPayload
): Promise<RegisterResponse> {
  return fetchWithAuth(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function registerFromInvitation(
  payload: RegisterFromInvitationPayload
): Promise<RegisterResponse> {
  return fetchWithAuth(`${API_BASE_URL}/invitations/register`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function logout(): Promise<{ message: string }> {
  return fetchWithAuth(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
  });
}

export async function requestPasswordReset(
  email: string
): Promise<{ message: string }> {
  return fetchWithoutAuth(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(
  token: string,
  newPassword: string
): Promise<{ message: string }> {
  return fetchWithoutAuth(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
}

export async function getCurrentUser(): Promise<User | null> {
  try {
    const response = await fetchWithAuth<LoginResponse>(
      `${API_BASE_URL}/auth/me`,
      { skipRedirectOn401: true, timeoutMs: 5_000 }
    );
    return response.user;
  } catch (error) {
    return null;
  }
}

export async function submitUserIntentSurvey({
  response,
}: {
  response: string;
}): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/surveys/user-intent`, {
    method: 'POST',
    body: JSON.stringify({ response }),
  });
}

export async function submitInvitePatientInterest(
  referralId: string
): Promise<{ success: boolean }> {
  return fetchWithAuth(`${API_BASE_URL}/surveys/invite-patient-interest`, {
    method: 'POST',
    body: JSON.stringify({ referralId }),
  });
}

export async function submitEmrInterest(data: {
  emrName: string;
}): Promise<{ success: boolean }> {
  return fetchWithAuth(`${API_BASE_URL}/surveys/emr-interest`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function submitContactSupport(data: {
  message: string;
  category?: string;
}): Promise<{ success: boolean }> {
  return fetchWithAuth(`${API_BASE_URL}/surveys/contact-support`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * The clinic fields a client may write. Shared by create + update so the two
 * payloads can't drift. `category` is optional here (a partial update need not
 * touch it) and re-required in the create payload.
 */
export interface ClinicWritableFields {
  name?: string;
  category?: string;
  website?: string;
  gUrl?: string;
  gBusinessStatus?: string | null;
  excludeFromExport?: number | null;
  address?: string;
  address1?: string;
  address2?: string | null;
  phone?: string;
  email?: string;
  faxNumber?: string;
  faxNumber2?: string;
  recordsRequestFaxNumber?: string;
  recordsRequestSubmissionMethod?: RecordsRequestSubmissionMethod | null;
  recordsRequestPortalUrl?: string;
  recordsRequestPortalUsername?: string;
  recordsRequestPortalNotes?: string;
  xrayWalkInHours?: string;
  mammogramWalkInHours?: string;
  businessHours?: string;
  procedures?: string[];
  excludedProcedures?: string[];
  // Admin-only: the API rejects them for other callers.
  isDesignPartner?: boolean;
  acceptsCardOverPhone?: boolean | null;
  internalNotes?: string;
  consumerNotes?: string;
  description?: string;
  emrUsed?: string;
  callsPtAfterReceivingFax?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  neighborhood?: string;
  latitude?: string;
  longitude?: string;
}

export async function updateClinicInfo({
  clinicId,
  ...rest
}: ClinicWritableFields & { clinicId: string }): Promise<ClinicInfo> {
  const response = await fetchWithAuth<ClinicInfo>(
    `${API_BASE_URL}/clinics/${clinicId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(rest),
    }
  );
  return response;
}

export async function createClinicInfo(
  // category + organizationId are required by the API (the export pipeline
  // filters on category and inner-joins organizations).
  payload: ClinicWritableFields & { category: string; organizationId: string }
): Promise<ClinicInfo> {
  return fetchWithAuth<ClinicInfo>(`${API_BASE_URL}/clinics`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function searchOrganizations(searchParams: {
  query: string;
  page?: number;
  limit?: number;
}): Promise<{ data: OrganizationSummary[]; total: number }> {
  const params = new URLSearchParams();
  if (searchParams.query.trim()) {
    params.append('query', searchParams.query.trim());
  }
  if (searchParams.page) {
    params.append('page', searchParams.page.toString());
  }
  if (searchParams.limit) {
    params.append('limit', searchParams.limit.toString());
  }

  return fetchWithAuth<{ data: OrganizationSummary[]; total: number }>(
    `${API_BASE_URL}/organizations/search?${params.toString()}`
  );
}

/**
 * Create an organization so the clinic-create flow isn't dead-ended when the
 * directory has no row for a practice yet. Returns the same summary shape as
 * search, so the picker can select the result directly. Rejects with a 409
 * message when the website domain already belongs to another organization.
 */
export async function createOrganization(payload: {
  name: string;
  websiteDomain?: string;
}): Promise<OrganizationSummary> {
  return fetchWithAuth<OrganizationSummary>(`${API_BASE_URL}/organizations`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ---- Accepted insurances (admin clinic detail page) ----

export interface InsuranceSummary {
  id: string;
  name: string;
}

export interface AcceptedInsurance extends InsuranceSummary {
  // 'website_scrape' (insurance scraper) or 'manual_admin' (admin edit).
  source: string | null;
}

export interface ClinicAcceptedInsurances {
  insurances: AcceptedInsurance[];
  // Org-level list the public clinic page falls back to while the clinic has
  // no clinic-level rows of its own; empty once clinic-level state exists.
  inheritedOrganizationInsurances: InsuranceSummary[];
  // Live clinicCount backing the "apply to all N clinics" checkbox — required
  // here, unlike the base OrganizationSummary where it's optional.
  organization: (OrganizationSummary & { clinicCount: number }) | null;
}

export async function getClinicAcceptedInsurances(
  clinicId: string
): Promise<ClinicAcceptedInsurances> {
  return fetchWithAuth<ClinicAcceptedInsurances>(
    `${API_BASE_URL}/clinics/${clinicId}/accepted-insurances`
  );
}

/**
 * Delta-based save: only the ids added/removed relative to the list at edit
 * start. With applyToOrganization the API fans the same delta out to every
 * clinic in the org plus the org-level canonical list.
 */
export async function updateClinicAcceptedInsurances({
  clinicId,
  ...rest
}: {
  clinicId: string;
  addedInsuranceIds: string[];
  removedInsuranceIds: string[];
  applyToOrganization: boolean;
}): Promise<{
  updatedClinicCount: number;
  addedCount: number;
  removedCount: number;
}> {
  return fetchWithAuth(
    `${API_BASE_URL}/clinics/${clinicId}/accepted-insurances`,
    {
      method: 'PUT',
      body: JSON.stringify(rest),
    }
  );
}

// ---- Cash-pay prices (admin clinic detail page) ----

/** One priced line: modality + optional detail + optional body part. */
export interface CashPriceRow {
  // `prices.id`. Stable across edits; absent on a row the admin just added.
  id: string;
  procedure: string;
  subCategory: string | null;
  bodyPart: string | null;
  // Decimal string ("2500.00") — not a number, so cents survive the round trip.
  price: string;
  notes: string | null;
  labelKind: CashPriceLabelKind;
}

// How a modality label behaves: 'published' reaches the public clinic page,
// 'reference' is a study label kept internal on purpose, 'unrecognized' is
// probably a misspelling. Computed by the API; drives the editor's copy.
export type CashPriceLabelKind = 'published' | 'reference' | 'unrecognized';

export interface ClinicCashPrices {
  prices: CashPriceRow[];
}

export interface CashPriceInput {
  procedure: string;
  subCategory: string | null;
  bodyPart: string | null;
  price: number;
  notes: string | null;
}

/**
 * Type-ahead suggestions for each field (database vocabulary plus the
 * always-suggested study labels). Global, not clinic-scoped, and suggestions
 * only — the fields stay free text.
 */
export interface CashPriceOptions {
  procedures: string[];
  subCategories: string[];
  bodyParts: string[];
  // The modality spellings the WordPress export publishes, for the warning's
  // "it accepts: …" copy.
  approvedProcedures: string[];
  // Every label whose kind isn't 'unrecognized', classified by the API so
  // the editor looks a typed label up instead of restating the rule.
  knownLabels: { label: string; kind: CashPriceLabelKind }[];
}

export async function getCashPriceOptions(): Promise<CashPriceOptions> {
  return fetchWithAuth<CashPriceOptions>(
    `${API_BASE_URL}/clinics/cash-price-options`
  );
}

export async function getClinicCashPrices(
  clinicId: string
): Promise<ClinicCashPrices> {
  return fetchWithAuth<ClinicCashPrices>(
    `${API_BASE_URL}/clinics/${clinicId}/cash-prices`
  );
}

/**
 * Full replacement of this clinic's admin-entered prices: any line not in the
 * payload is deleted, which is how a row is removed. Scraped prices are never
 * touched. The API creates a catalogue row on demand for any
 * modality/detail/body-part combination it hasn't seen before.
 */
export async function updateClinicCashPrices({
  clinicId,
  prices,
}: {
  clinicId: string;
  prices: CashPriceInput[];
}): Promise<{ savedCount: number; removedCount: number }> {
  return fetchWithAuth(`${API_BASE_URL}/clinics/${clinicId}/cash-prices`, {
    method: 'PUT',
    body: JSON.stringify({ prices }),
  });
}

export async function searchInsurances(searchParams: {
  query: string;
  limit?: number;
}): Promise<{ data: InsuranceSummary[] }> {
  const params = new URLSearchParams();
  if (searchParams.query.trim()) {
    params.append('query', searchParams.query.trim());
  }
  if (searchParams.limit) {
    params.append('limit', searchParams.limit.toString());
  }

  return fetchWithAuth<{ data: InsuranceSummary[] }>(
    `${API_BASE_URL}/insurances/search?${params.toString()}`
  );
}

/**
 * Add a payer to the master insurances list. Idempotent on name: if the exact
 * name already exists (case-insensitive) the API returns that row with
 * `created: false` instead of creating a duplicate.
 */
export async function createInsurance(
  name: string
): Promise<InsuranceSummary & { created: boolean }> {
  return fetchWithAuth<InsuranceSummary & { created: boolean }>(
    `${API_BASE_URL}/insurances`,
    {
      method: 'POST',
      body: JSON.stringify({ name }),
    }
  );
}

/**
 * Saves the referral's Referring Provider card, creating it when missing.
 * `directoryProviderId` links that directory entry; null drops the current
 * link so the card's details decide it (NPI, same name and fax, or a new
 * staff entry), as they do for an unlinked card.
 */
export async function updateReferringProvider({
  referralId,
  ...rest
}: {
  referralId: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
  npi?: string;
  phoneNumber?: string;
  faxNumber?: string;
  email?: string;
  address?: string;
  directoryProviderId?: string | null;
  practiceAddress?: {
    addressLine1?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}): Promise<void> {
  await fetchWithAuth(
    `${API_BASE_URL}/referrals/${referralId}/referring-provider`,
    {
      method: 'PATCH',
      body: JSON.stringify(rest),
    }
  );
}

export async function updateUserProfile(updateData: {
  firstName?: string;
  lastName?: string;
  workPhoneNumber?: string;
  titleOrRole?: string;
}): Promise<User> {
  const response = await fetchWithAuth<LoginResponse>(
    `${API_BASE_URL}/auth/me`,
    {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    }
  );
  return response.user;
}

export async function updateUserPassword(passwordData: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ message: string }> {
  return fetchWithAuth(`${API_BASE_URL}/auth/me/password`, {
    method: 'PATCH',
    body: JSON.stringify(passwordData),
  });
}

export async function fetchAllReferrals(params?: {
  page?: number;
  limit?: number;
  clinicId?: string;
  /** Only valid together with clinicId — the API rejects direction alone. */
  direction?: ReferralDirection;
  coordinatorTab?: string;
  procedure?: string;
  followUp?: FollowUpOutcome;
  /** Only referrals the patient paid money on (cash charge or request fee). */
  paid?: boolean;
  /** Split paid referrals out into `pinnedPaid` (page 1); exclusive with `paid`. */
  pinPaid?: boolean;
}): Promise<AdminReferralListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) {
    searchParams.append('page', params.page.toString());
  }
  if (params?.limit) {
    searchParams.append('limit', params.limit.toString());
  }
  if (params?.clinicId) {
    searchParams.append('clinicId', params.clinicId);
  }
  if (params?.direction) {
    searchParams.append('direction', params.direction);
  }
  if (params?.coordinatorTab) {
    searchParams.append('coordinatorTab', params.coordinatorTab);
  }
  if (params?.procedure) {
    searchParams.append('procedure', params.procedure);
  }
  if (params?.followUp) {
    searchParams.append('followUp', params.followUp);
  }
  if (params?.paid) {
    searchParams.append('paid', 'true');
  }
  if (params?.pinPaid) {
    searchParams.append('pinPaid', 'true');
  }

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/referrals${queryString ? `?${queryString}` : ''}`;

  return fetchWithAuth<AdminReferralListResponse>(url);
}

export type ReferralDirection = 'inbound' | 'outbound';

export type { ReferralStatus };

export interface ReferralListOptions {
  page?: number;
  limit?: number;
  direction?: ReferralDirection;
  status?: ReferralStatus;
  type?: string;
  search?: string;
  needsAttention?: boolean;
}

function referralListParams(options: ReferralListOptions = {}) {
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page.toString());
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.direction) params.append('direction', options.direction);
  if (options.status) params.append('status', options.status);
  if (options.type) params.append('type', options.type);
  if (options.search) params.append('search', options.search);
  if (options.needsAttention) params.append('needsAttention', 'true');
  return params;
}

export async function fetchReferrals(
  clinicId: string,
  options?: ReferralListOptions
): Promise<ReferralListResponse> {
  if (!clinicId) {
    // Basic client-side check, though UI should prevent this
    throw new Error('Clinic ID is required to fetch referrals.');
  }

  const queryString = referralListParams(options).toString();
  return fetchWithAuth<ReferralListResponse>(
    `${API_BASE_URL}/clinics/${clinicId}/referrals${queryString ? `?${queryString}` : ''}`
  );
}

/**
 * The referral list across every clinic the user belongs to, or only the
 * `clinicIds` among them (empty = all).
 */
export async function fetchMyClinicsReferrals(
  options: ReferralListOptions & { clinicIds?: string[] } = {}
): Promise<ReferralListResponse> {
  const { clinicIds, ...listOptions } = options;
  const params = referralListParams(listOptions);
  if (clinicIds?.length) params.append('clinicIds', clinicIds.join(','));
  const queryString = params.toString();
  return fetchWithAuth<ReferralListResponse>(
    `${API_BASE_URL}/my-clinics/referrals${queryString ? `?${queryString}` : ''}`
  );
}

export async function fetchUsers(
  clinicId: string,
  pagination?: { page?: number; limit?: number }
): Promise<PaginatedResponse<UserListItemDto>> {
  if (!clinicId) {
    // Basic client-side check, though UI should prevent this
    throw new Error('Clinic ID is required to fetch users.');
  }

  // Create query params for pagination only
  const params = new URLSearchParams();
  if (pagination?.page) {
    params.append('page', pagination.page.toString());
  }
  if (pagination?.limit) {
    params.append('limit', pagination.limit.toString());
  }

  // Build URL with path parameter for clinicId
  const queryString = params.toString();
  const url = `${API_BASE_URL}/clinics/${clinicId}/users${queryString ? `?${queryString}` : ''}`;

  // Uses existing fetchWithAuth
  return fetchWithAuth<PaginatedResponse<UserListItemDto>>(url);
}

export async function fetchInvitations(
  clinicId: string,
  pagination?: { page?: number; limit?: number }
): Promise<PaginatedResponse<InvitationResponseDto>> {
  if (!clinicId) {
    // Basic client-side check, though UI should prevent this
    throw new Error('Clinic ID is required to fetch invitations.');
  }

  // Create query params for pagination only
  const params = new URLSearchParams();
  if (pagination?.page) {
    params.append('page', pagination.page.toString());
  }
  if (pagination?.limit) {
    params.append('limit', pagination.limit.toString());
  }

  // Build URL with path parameter for clinicId
  const queryString = params.toString();
  const url = `${API_BASE_URL}/clinics/${clinicId}/invitations${queryString ? `?${queryString}` : ''}`;

  // Uses existing fetchWithAuth
  return fetchWithAuth<PaginatedResponse<InvitationResponseDto>>(url);
}

export async function createInvitation({
  clinicId,
  email,
}: {
  clinicId: string;
  email: string;
}): Promise<InvitationResponseDto> {
  if (!clinicId) {
    throw new Error('Clinic ID is required to create an invitation.');
  }

  return fetchWithAuth(`${API_BASE_URL}/clinics/${clinicId}/invitations`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function getInvitationLink(
  invitationId: string
): Promise<{ invitationLink: string }> {
  return fetchWithAuth(`${API_BASE_URL}/invitations/${invitationId}/link`);
}

export async function verifyInvitation(
  token: string
): Promise<VerifyInvitationResponseDto> {
  if (!token) {
    throw new Error('Invitation token is required for verification.');
  }

  return fetchWithAuth(`${API_BASE_URL}/invitations/verify`, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function deleteInvitation(
  invitationId: string
): Promise<InvitationResponseDto> {
  if (!invitationId) {
    throw new Error('Invitation ID is required to delete an invitation.');
  }

  return fetchWithAuth(`${API_BASE_URL}/invitations/${invitationId}`, {
    method: 'DELETE',
  });
}

export async function resendInvitation(
  invitationId: string
): Promise<InvitationResponseDto> {
  if (!invitationId) {
    throw new Error('Invitation ID is required to resend an invitation.');
  }

  return fetchWithAuth(`${API_BASE_URL}/invitations/${invitationId}/resend`, {
    method: 'POST',
  });
}

export async function generateReferralUploadUrl(
  payload: UploadRequestPayload
): Promise<UploadUrlResponse> {
  return fetchWithAuth(`${API_BASE_URL}/referrals/upload-url`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface BookingResult {
  id: string;
  chargedAmountInCents?: number;
  /**
   * True when the request is linked to the booker's patient user, so the
   * booking email can sign in to see it; false for a staff email. Absent
   * from an API that predates the account offer.
   */
  requestedByUser?: boolean;
}

/**
 * Anonymous booking-wizard submit (booking migration M8). No API key and no
 * auth cookie — the caller is a patient in an iframe — and the response carries
 * only the new referral id, never patient data.
 */
export async function submitBooking(
  payload: BookingPayload,
  turnstileToken?: string
): Promise<BookingResult> {
  return fetchWithoutAuth<BookingResult>(
    `${API_BASE_URL}/public-referrals/booking`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      // With a card attached the API may charge it inside a transaction
      // allowed 30 s; aborting earlier would leave a charged card behind an
      // "outcome unknown" screen.
      ...(payload.stripePaymentMethodId
        ? { timeoutMs: CASH_CHARGE_BOOKING_TIMEOUT_MS }
        : {}),
      // Monitor-only on the API side, so an absent token is an ordinary
      // request — the header is simply omitted (booking migration M9).
      ...(turnstileToken
        ? { headers: { [TURNSTILE_TOKEN_HEADER]: turnstileToken } }
        : {}),
    }
  );
}

/**
 * Anonymous PI-lead submit (slice C). Nothing is persisted server-side —
 * delivery failure is a 503 and retrying is safe by contract.
 */
export async function submitInjuryLead(
  payload: InjuryLeadPayload
): Promise<{ success: boolean }> {
  return fetchWithoutAuth<{ success: boolean }>(
    `${API_BASE_URL}/injury-leads`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

export async function generateFaxUploadUrl(
  payload: UploadRequestPayload,
  { signal }: { signal?: AbortSignal } = {}
): Promise<UploadUrlResponse> {
  return fetchWithoutAuth(`${API_BASE_URL}/faxes/upload-url`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal,
  });
}

/**
 * The presigned PUT was answered with an error. `s3Code` is the XML `<Code>`
 * S3 puts in every error body, and `responseMediaType` the response's media
 * type (`application/xml` from S3). A 4xx with no code did not come from S3:
 * a proxy or endpoint-security product between the browser and S3 answered
 * in its place, usually with an HTML block page. The 2026-10-07 case is in
 * docs/booking-migration/booking-uploads.md.
 */
export class S3UploadError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly s3Code: string | null,
    readonly responseMediaType: string | null
  ) {
    super(message);
    this.name = 'S3UploadError';
  }

  /** The refusal is final: S3 would have said why, and whatever did answer will again. */
  get isNotFromS3(): boolean {
    return this.status < 500 && this.s3Code === null;
  }

  /** From the error response: its status, body and `Content-Type` header. */
  static fromResponse(
    status: number,
    body: string,
    contentType: string | null
  ): S3UploadError {
    return new S3UploadError(
      `S3 Upload Failed: ${status} - ${body || 'Unknown error'}`,
      status,
      body.match(/<Code>([^<]+)<\/Code>/)?.[1] ?? null,
      // `text/html; charset=utf-8` → `text/html`.
      contentType?.split(';')[0].trim().toLowerCase() || null
    );
  }
}

/**
 * PUT a file to a presigned S3 URL.
 *
 * `signedContentType` is whatever `contentType` the upload-url response came
 * back with. When the API bound a content type into the signature, S3 compares
 * the header byte-for-byte and rejects a mismatch — so the signed value has to
 * win over `file.type`, which the browser derives independently and spells
 * differently often enough to matter.
 */
export async function uploadFileToS3(
  presignedUrl: string,
  file: File,
  signedContentType?: string
): Promise<Response> {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': signedContentType ?? file.type,
    },
  });

  if (!response.ok) {
    throw S3UploadError.fromResponse(
      response.status,
      await response.text(),
      response.headers.get('Content-Type')
    );
  }
  return response;
}

export interface S3PutOptions {
  signal?: AbortSignal;
  /** Fraction of the body sent, 0–1. */
  onProgress?: (fraction: number) => void;
  /** Give up when no bytes move (and no answer comes) for this long. */
  stallTimeoutMs?: number;
}

/**
 * {@link uploadFileToS3} over XHR: `fetch` reports no upload progress, so it
 * can neither drive a progress bar nor tell a slow upload from a dead one.
 * A network failure rejects with a `TypeError`, as `fetch` does; a stall with
 * a `TimeoutError` and an abort with an `AbortError` (both `DOMException`s).
 */
export function putFileToS3(
  presignedUrl: string,
  file: Blob,
  signedContentType: string | undefined,
  { signal, onProgress, stallTimeoutMs }: S3PutOptions = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;

    const finish = (outcome: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(stallTimer);
      signal?.removeEventListener('abort', onAbort);
      outcome();
    };
    const fail = (error: Error) => {
      finish(() => reject(error));
      xhr.abort();
    };
    const onAbort = () =>
      fail(new DOMException('Upload aborted', 'AbortError'));
    const armStallTimer = () => {
      if (!stallTimeoutMs) return;
      clearTimeout(stallTimer);
      stallTimer = setTimeout(
        () =>
          fail(
            new DOMException(
              `No upload progress for ${stallTimeoutMs / 1000} s`,
              'TimeoutError'
            )
          ),
        stallTimeoutMs
      );
    };

    signal?.addEventListener('abort', onAbort);
    xhr.open('PUT', presignedUrl);
    const contentType = signedContentType ?? file.type;
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = event => {
      armStallTimer();
      if (event.lengthComputable && event.total) {
        onProgress?.(event.loaded / event.total);
      }
    };
    xhr.onload = () =>
      finish(() => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }
        reject(
          S3UploadError.fromResponse(
            xhr.status,
            xhr.responseText,
            xhr.getResponseHeader('Content-Type')
          )
        );
      });
    xhr.onerror = () =>
      finish(() => reject(new TypeError('Network error during upload')));

    armStallTimer();
    xhr.send(file);
  });
}

export async function sendFax(
  payload: SendFaxPayload
): Promise<SendFaxResponse> {
  return fetchWithAuth(`${API_BASE_URL}/referrals/send`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * The recipient identity for re-routing — a known clinic or a manual fax
 * destination. Mirrors the API's AddReferralRecipientDto.
 *
 * `dispatch: false` records the destination WITHOUT faxing the packet to it —
 * for a clinic that already has the documents (e.g. faxed manually, or the
 * clinic record is being corrected after the fact), where a second fax would be
 * a duplicate the clinic has to reconcile. Omitted means send, as before.
 */
type RecipientDispatchOption = { dispatch?: boolean };

export type ReferralRecipientPayload = RecipientDispatchOption &
  (
    | { recipientType: 'clinic'; recipientClinicId: string }
    | {
        recipientType: 'faxNumber';
        faxNumber: string;
        recipientOrganization: string;
        recipientName?: string;
      }
  );

/**
 * Re-route a referral from one recipient to another: the old recipient is
 * withdrawn (retained as history) and a new live recipient is added, then
 * faxed unless the payload sets `dispatch: false`. Admin only. Returns the
 * fresh referral detail.
 */
export async function reRouteReferralRecipient(
  referralId: string,
  recipientId: string,
  payload: ReferralRecipientPayload
): Promise<ReferralDetailResponse> {
  return fetchWithAuth(
    `${API_BASE_URL}/referrals/${referralId}/recipients/${recipientId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );
}

/**
 * Remove a recipient from a referral (withdrawn, row retained). Rejected if it
 * is the only live recipient. Admin only. Returns the fresh referral detail.
 */
export async function removeReferralRecipient(
  referralId: string,
  recipientId: string
): Promise<ReferralDetailResponse> {
  return fetchWithAuth(
    `${API_BASE_URL}/referrals/${referralId}/recipients/${recipientId}`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Re-fax the referral's current documents to an existing live recipient (the
 * "send another document to this recipient" flow). Targets that specific
 * recipient. Admin only. Returns the fresh referral detail.
 */
export async function resendReferralToRecipient(
  referralId: string,
  recipientId: string
): Promise<ReferralDetailResponse> {
  return fetchWithAuth(
    `${API_BASE_URL}/referrals/${referralId}/recipients/${recipientId}/resend`,
    {
      method: 'POST',
    }
  );
}

/**
 * Fax the request for the physician's order to the fax number on the
 * referral's Referring Provider card. Admin only.
 */
export interface OrderRequestSend {
  subject: string;
  message: string;
  /** The referral's documents to fax after the cover page, in order. */
  documentIds: string[];
  /** Staff confirm the patient agreed to us contacting their provider. */
  patientConsentConfirmed?: boolean;
}

/** Saves files to fax the provider with an order request; never sent to the clinic. */
export async function addOrderRequestDocuments(
  referralId: string,
  s3Keys: string[]
): Promise<{ ids: string[] }> {
  return fetchWithAuth(
    `${API_BASE_URL}/referrals/${referralId}/order-request/documents`,
    {
      method: 'POST',
      body: JSON.stringify({ s3Keys }),
    }
  );
}

export async function sendOrderRequestToProvider(
  referralId: string,
  request: OrderRequestSend
): Promise<{ faxId: string }> {
  return fetchWithAuth(
    `${API_BASE_URL}/referrals/${referralId}/order-request`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

export type GeneratedOrderSignatureMode = 'SIGN_NOW' | 'SEND_FOR_SIGNATURE';

export interface CreateGeneratedOrderReferralPayload {
  senderClinicId: string;
  recipientType: 'clinic' | 'faxNumber';
  recipientClinicId?: string;
  faxNumber?: string;
  recipientName?: string;
  recipientOrganization?: string;
  patientInfo: {
    firstName: string;
    lastName: string;
    email?: string;
    phoneNumber: string;
    address?: string;
    dateOfBirth: string;
    sex: string;
    heightFt?: number;
    heightIn?: number;
    weightLbs?: number;
  };
  referringProviderInfo: {
    honorific?: string;
    firstName: string;
    lastName: string;
    organizationName?: string;
    email: string;
    phoneNumber?: string;
    npi: string;
  };
  orderDetails: {
    imagingStudy: string;
    bodyPart?: string;
    diagnosis?: string;
    diagnosisCode?: string;
    notes?: string;
  };
  paymentOptions: {
    paymentMethod: string;
    paymentMethodOther?: string;
  };
  insuranceInfo?: {
    policyName?: string;
    policyNumber?: string;
    groupNumber?: string;
    relationshipToHolder?: string;
    policyHolderName?: string;
  };
  signatureMode: GeneratedOrderSignatureMode;
  signatureEmail?: string;
}

export interface CreateGeneratedOrderReferralResponse {
  referralId: string;
  eSignatureRequestId: string;
  status: string;
  signatureMode: GeneratedOrderSignatureMode;
}

export async function createGeneratedOrderReferral(
  payload: CreateGeneratedOrderReferralPayload
): Promise<CreateGeneratedOrderReferralResponse> {
  return fetchWithAuth(`${API_BASE_URL}/referrals/create-order`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function generateViewUrl(s3Key: string): Promise<ViewUrlResponse> {
  if (!s3Key) {
    throw new Error('S3 key is required to generate a view URL.');
  }
  const queryParams = new URLSearchParams({ s3Key });

  return fetchWithAuth(
    `${API_BASE_URL}/referrals/view-url?${queryParams.toString()}`
  );
}

export async function extractOrderData(
  payload: ExtractAndCreatePayload
): Promise<ExtractAndCreateData> {
  return fetchWithAuth(`${API_BASE_URL}/referrals/extract-and-create`, {
    method: 'POST',
    body: JSON.stringify(payload),
    timeoutMs: RENDER_CREATE_TIMEOUT_MS,
  });
}

export async function checkEligibility(
  payload: EligibilityCheckPayload
): Promise<EligibilityInfo> {
  return fetchWithAuth(`${API_BASE_URL}/insurance/eligibility`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function checkNpi(
  payload: NpiCheckPayload
): Promise<NpiCheckInfo> {
  return fetchWithAuth(`${API_BASE_URL}/providers/npi-check`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getMyClinics(): Promise<ClinicBasicInfo[]> {
  // Uses existing fetchWithAuth
  return fetchWithAuth<ClinicBasicInfo[]>(`${API_BASE_URL}/auth/me/clinics`);
}

export async function getClinicById(clinicId: string): Promise<ClinicInfo> {
  // Encoded: the booking wizard passes this straight from a URL query param,
  // where `..%2F..%2F` would otherwise normalize to a different endpoint.
  return fetchWithoutAuth<ClinicInfo>(
    `${API_BASE_URL}/clinics/${encodeURIComponent(clinicId)}`
  );
}

/**
 * A clinic's own settings fields (email, fax numbers, directory opt-in). Members
 * only, so it must send the session cookie — unlike the public getClinicById.
 */
export async function getClinicSettings(clinicId: string): Promise<ClinicInfo> {
  return fetchWithAuth<ClinicInfo>(
    `${API_BASE_URL}/clinics/${encodeURIComponent(clinicId)}/settings`
  );
}

/** The booking wizard's cash price for a study at a clinic; `price` null = none. */
export async function getCashPrice(
  clinicId: string,
  study: string,
  contrast?: string
): Promise<{ price: CashPrice | null }> {
  const params = new URLSearchParams({ clinicId, study });
  if (contrast) params.set('contrast', contrast);
  return fetchWithoutAuth<{ price: CashPrice | null }>(
    `${API_BASE_URL}/public-referrals/cash-price?${params}`
  );
}

/** Whether a self-referring booking for this study is held for an order. */
export async function getOrderRequirement(
  study: string
): Promise<{ requiresOrder: boolean }> {
  const params = new URLSearchParams({ study });
  return fetchWithoutAuth<{ requiresOrder: boolean }>(
    `${API_BASE_URL}/public-referrals/order-requirement?${params}`
  );
}

/**
 * Public NPPES provider directory search, used by the booking wizard's
 * referring-provider typeahead. Unauthenticated by design (the route returns
 * only public NPPES data and carries its own 30/min per-IP throttle), so it
 * goes through fetchWithoutAuth — the wizard's caller is an anonymous patient.
 */
export async function searchProviderDirectory(
  query: string,
  limit = 10
): Promise<ProviderSearchResult[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const response = await fetchWithoutAuth<{
    providers: ProviderSearchResult[];
  }>(`${API_BASE_URL}/providers/search?${params.toString()}`);
  return response.providers;
}

/**
 * Directory entries matching a referring provider: the fax number's entries
 * (any state) first, then name matches. Admin only.
 */
export async function findDirectoryMatches(params: {
  q?: string;
  state?: string;
  fax?: string;
}): Promise<DirectoryMatch[]> {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.state) search.set('state', params.state);
  if (params.fax) search.set('fax', params.fax);
  const response = await fetchWithAuth<{ providers: DirectoryMatch[] }>(
    `${API_BASE_URL}/providers/directory-matches?${search.toString()}`
  );
  return response.providers;
}

/**
 * Admin-only clinic read that includes records-request portal credentials
 * (the public getClinicById redacts them). Requires an authenticated admin.
 */
export async function getClinicByIdForAdmin(
  clinicId: string
): Promise<ClinicInfo> {
  return fetchWithAuth<ClinicInfo>(`${API_BASE_URL}/clinics/${clinicId}/admin`);
}

/**
 * How many referrals/records requests are blocked waiting on this clinic's fax
 * number. Used to warn coordinators that adding a fax will re-send those items.
 */
export async function getClinicBlockedByMissingFaxCount(
  clinicId: string
): Promise<number> {
  const response = await fetchWithAuth<{ count: number }>(
    `${API_BASE_URL}/clinics/${clinicId}/blocked-by-missing-fax-count`
  );
  return response.count;
}

export async function searchClinics(searchParams: {
  query?: string;
  page?: number;
  limit?: number;
  latitude?: number | null;
  longitude?: number | null;
  // Restrict to these destination clinic categories (map browser).
  categories?: string[];
  // Bound geo results to this many miles of latitude/longitude (map browser).
  radiusMiles?: number;
  // Opt out of the default operational-only filter (e.g. admin clinic
  // management, which must see closed/non-operational clinics too).
  includeNonOperational?: boolean;
  // Result ordering for geo searches (map browser): nearest or highest-rated.
  sortBy?: 'distance' | 'rating';
  // Use the admin-only endpoint that also returns clinics hidden from the
  // directory (excludeFromExport = 1, e.g. provider-signup clinics). Requires
  // an ADMIN session — non-admins get a 403.
  adminScope?: boolean;
}): Promise<{ data: ClinicInfo[]; total: number }> {
  const params = new URLSearchParams();

  if (searchParams.query?.trim()) {
    params.append('query', searchParams.query.trim());
  }
  if (searchParams.categories && searchParams.categories.length > 0) {
    params.append('categories', searchParams.categories.join(','));
  }
  if (searchParams.radiusMiles !== undefined) {
    params.append('radiusMiles', searchParams.radiusMiles.toString());
  }
  if (searchParams.sortBy) {
    params.append('sortBy', searchParams.sortBy);
  }
  if (searchParams.includeNonOperational) {
    params.append('includeNonOperational', 'true');
  }
  if (searchParams.page) {
    params.append('page', searchParams.page.toString());
  }
  if (searchParams.limit) {
    params.append('limit', searchParams.limit.toString());
  }
  if (
    searchParams.latitude !== null &&
    searchParams.latitude !== undefined &&
    searchParams.longitude !== null &&
    searchParams.longitude !== undefined
  ) {
    params.append('latitude', searchParams.latitude.toString());
    params.append('longitude', searchParams.longitude.toString());
  }

  const queryString = params.toString();
  const path = searchParams.adminScope
    ? '/clinics/search/admin'
    : '/clinics/search';
  const url = `${API_BASE_URL}${path}${queryString ? `?${queryString}` : ''}`;

  return fetchWithAuth<{ data: ClinicInfo[]; total: number }>(url);
}

export async function getRecentRecipients(
  clinicId: string
): Promise<{ data: ClinicInfo[] }> {
  return fetchWithAuth<{ data: ClinicInfo[] }>(
    `${API_BASE_URL}/clinics/${clinicId}/recent-recipients`
  );
}

/**
 * Converts form data structure to the API's expected structure
 * based on the ClinicReferralPayloadDto structure
 */
function convertFormToApiPayload(formData: ReferralFormValues) {
  return {
    patient: {
      firstName: formData.patient.firstName,
      lastName: formData.patient.lastName,
      middleName: formData.patient.middleName,
      dateOfBirth: formData.patient.dateOfBirth,
      phoneNumber: formData.patient.phoneNumber,
      email: formData.patient.email,
      address: formData.patient.address,
      sex: formData.patient.sex,
      heightFt: formData.patient.heightFt,
      heightIn: formData.patient.heightIn,
      weightLbs: formData.patient.weightLbs,
      ssn: formData.patient.ssn,
      mrn: formData.patient.mrn,
      insurancePrimary: {
        company: formData.insurance?.primary?.company,
        groupNumber: formData.insurance?.primary?.groupNumber,
        subscriberId: formData.insurance?.primary?.subscriberId,
        subscriberRelationship:
          formData.insurance?.primary?.subscriberRelationship,
        subscriberName: formData.insurance?.primary?.subscriberName,
      },
      insuranceSecondary: {
        company: formData.insurance?.secondary?.company,
        groupNumber: formData.insurance?.secondary?.groupNumber,
        subscriberId: formData.insurance?.secondary?.subscriberId,
        subscriberRelationship:
          formData.insurance?.secondary?.subscriberRelationship,
        subscriberName: formData.insurance?.secondary?.subscriberName,
      },
    },
    referringProvider: formData.referringProvider
      ? {
          honorific: formData.referringProvider.honorific,
          firstName: formData.referringProvider.firstName,
          lastName: formData.referringProvider.lastName,
          organization: formData.referringProvider.organization,
          address: formData.referringProvider.address,
          phoneNumber: formData.referringProvider.phoneNumber,
          email: formData.referringProvider.email,
          faxNumber: formData.referringProvider.faxNumber,
          npi: formData.referringProvider.npi,
        }
      : null,
    order: formData.order
      ? {
          diagnosis: formData.order.diagnosis,
          diagnosisCode: formData.order.diagnosisCode,
          diagnosisDetail: formData.order.diagnosisDetail,
          orderNumber: formData.order.orderNumber,
          orderDate: formData.order.orderDate,
          orderExpirationDate: formData.order.orderExpirationDate,
          imagingProcedure: formData.order.imagingProcedure,
          imagingProcedureUrgency: formData.order.imagingProcedureUrgency,
          copyTo: formData.order.copyTo,
          notes: formData.notes,
        }
      : null,
  };
}

/**
 * Saves a referral - creates a new one or updates an existing one based on referralId
 */
export async function saveReferral(
  data: ReferralFormValues,
  clinicId: string,
  referralId: string | null = null,
  orderDocumentKey: string | null = null
): Promise<ReferralResponse> {
  const apiData = convertFormToApiPayload(data);

  // Process the data to remove empty strings (convert to null)
  const processedData = processFormData(apiData);

  // Prepare the final payload
  const payload = referralId
    ? processedData // For updates (PUT), we don't need to include clinicId and orderDocumentKey
    : {
        ...processedData,
        clinicId,
        orderDocumentKey,
      };

  if (referralId) {
    // Update existing referral (PUT)
    return fetchWithAuth(`${API_BASE_URL}/referrals/${referralId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  } else {
    // Create new referral (POST) — renders the facesheet inline, so allow more time.
    return fetchWithAuth(`${API_BASE_URL}/referrals`, {
      method: 'POST',
      body: JSON.stringify(payload),
      timeoutMs: RENDER_CREATE_TIMEOUT_MS,
    });
  }
}

/**
 * Recursively processes form data to convert empty strings to null
 * This ensures we don't save empty strings to the database
 */
function processFormData(data: any): any {
  if (data === null || data === undefined) {
    return null;
  }

  if (typeof data === 'string') {
    return data.trim() === '' ? null : data;
  }

  if (Array.isArray(data)) {
    return data.map(item => processFormData(item));
  }

  if (typeof data === 'object') {
    const result: Record<string, any> = {};

    for (const [key, value] of Object.entries(data)) {
      result[key] = processFormData(value);
    }

    return result;
  }

  return data;
}

export async function getReferralById(
  referralId: string
): Promise<ReferralDetailResponse> {
  return fetchWithAuth(`${API_BASE_URL}/referrals/${referralId}`);
}

export async function updateReferralOrderDetails(
  referralId: string,
  orderUpdates: {
    imagingProcedure: string;
    bodyPart: string;
    imagingProcedureUrgency: string;
    diagnosisCode: string;
    diagnosis: string;
    referralType: string;
  }
): Promise<ReferralDetailResponse> {
  return fetchWithAuth(`${API_BASE_URL}/referrals/${referralId}/order`, {
    method: 'PATCH',
    body: JSON.stringify({
      imagingProcedure: orderUpdates.imagingProcedure || null,
      bodyPart: orderUpdates.bodyPart || null,
      imagingProcedureUrgency: orderUpdates.imagingProcedureUrgency || null,
      diagnosisCode: orderUpdates.diagnosisCode || null,
      diagnosis: orderUpdates.diagnosis || null,
      // Only sent when a value is present. Referral Type is admin-only and the
      // Select has no "clear" option, so an empty value means "not being edited"
      // — omit it rather than send null, which would wipe the existing type when
      // a non-admin saves other order fields from a form that never surfaced it.
      ...(orderUpdates.referralType
        ? { referralType: orderUpdates.referralType }
        : {}),
    }),
  });
}

export async function updateReferralPatientDetails(
  referralId: string,
  updates: {
    firstName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    sex?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
    address?: string | null;
  }
): Promise<ReferralDetailResponse> {
  return fetchWithAuth(`${API_BASE_URL}/referrals/${referralId}/patient`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function updateReferralInsuranceDetails(
  referralId: string,
  updates: {
    primaryCompany?: string | null;
    primarySubscriberId?: string | null;
    primaryGroupNumber?: string | null;
    primarySubscriberName?: string | null;
    primarySubscriberRelationship?: string | null;
    /** Policy holder DOB as YYYY-MM-DD; null clears it */
    primarySubscriberDateOfBirth?: string | null;
    secondaryCompany?: string | null;
    secondarySubscriberId?: string | null;
    secondaryGroupNumber?: string | null;
    secondarySubscriberName?: string | null;
    secondarySubscriberRelationship?: string | null;
  }
): Promise<ReferralDetailResponse> {
  return fetchWithAuth(`${API_BASE_URL}/referrals/${referralId}/insurance`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function getLatestNpiCheck(
  npi: string
): Promise<NpiCheckInfo | null> {
  return fetchWithAuth(`${API_BASE_URL}/providers/npi/${npi}/check/latest`);
}

export async function getLatestEligibilityCheck(
  insuranceCompany: string,
  subscriberName?: string,
  dateOfBirth?: string,
  subscriberId?: string
): Promise<EligibilityInfo | null> {
  if (!insuranceCompany) {
    return Promise.resolve(null);
  }

  // At least one of subscriberName or dateOfBirth must be provided
  if (!subscriberName && !dateOfBirth && !subscriberId) {
    return Promise.resolve(null);
  }

  const params = new URLSearchParams({
    insuranceName: insuranceCompany,
  });

  if (subscriberName) {
    params.append('subscriberName', subscriberName);
  }

  if (dateOfBirth) {
    params.append('dateOfBirth', dateOfBirth);
  }

  if (subscriberId) {
    params.append('memberId', subscriberId);
  }

  return fetchWithAuth(
    `${API_BASE_URL}/insurance/eligibility/latest?${params}`
  );
}

export async function fetchFaxes(
  clinicId: string,
  pagination?: { page?: number; limit?: number }
): Promise<PaginatedResponse<FaxListItem>> {
  if (!clinicId) {
    throw new Error('Clinic ID is required to fetch faxes.');
  }

  const params = new URLSearchParams();
  if (pagination?.page) {
    params.append('page', pagination.page.toString());
  }
  if (pagination?.limit) {
    params.append('limit', pagination.limit.toString());
  }

  const queryString = params.toString();
  const url = `${API_BASE_URL}/clinics/${clinicId}/faxes${queryString ? `?${queryString}` : ''}`;

  return fetchWithAuth<PaginatedResponse<FaxListItem>>(url);
}

export async function fetchAdminFaxes(
  direction: 'INBOUND' | 'OUTBOUND',
  pagination?: {
    page?: number;
    limit?: number;
    search?: string;
    linkStatus?: 'UNLINKED' | 'LINKED' | 'ALL';
  }
): Promise<PaginatedResponse<AdminFaxListItem>> {
  const params = new URLSearchParams();
  params.append('direction', direction);
  if (pagination?.page) {
    params.append('page', pagination.page.toString());
  }
  if (pagination?.limit) {
    params.append('limit', pagination.limit.toString());
  }
  if (pagination?.search) {
    params.append('search', pagination.search);
  }
  if (pagination?.linkStatus) {
    params.append('linkStatus', pagination.linkStatus);
  }

  const url = `${API_BASE_URL}/admin/faxes?${params.toString()}`;

  return fetchWithAuth<PaginatedResponse<AdminFaxListItem>>(url);
}

/**
 * Searches referrals and records requests by patient name for the inbound-fax
 * link picker.
 */
export async function searchLinkableRecords(
  query: string
): Promise<{ results: LinkableRecord[] }> {
  const params = new URLSearchParams({ q: query });
  return fetchWithAuth<{ results: LinkableRecord[] }>(
    `${API_BASE_URL}/admin/faxes/linkable-records?${params.toString()}`
  );
}

/** Links an inbound fax to a referral or records request. */
export async function linkInboundFax(
  faxId: string,
  recordId: string
): Promise<{ workflowType: 'REFERRAL' | 'MEDICAL_RECORD_REQUEST' }> {
  if (!faxId || !recordId) {
    throw new Error('Fax ID and record ID are required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/faxes/${faxId}/link`, {
    method: 'POST',
    body: JSON.stringify({ recordId }),
  });
}

/** Unlinks an inbound fax from its record. */
export async function unlinkInboundFax(
  faxId: string
): Promise<{ success: boolean }> {
  if (!faxId) {
    throw new Error('Fax ID is required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/faxes/${faxId}/link`, {
    method: 'DELETE',
  });
}

interface FaxLinkRef {
  recordId: string;
  workflowType: 'REFERRAL' | 'MEDICAL_RECORD_REQUEST';
}

/**
 * Soft-deletes a fax (inbound or sent): hides it from the lists and detaches it
 * from any linked record. Recoverable via restoreFax (Undo). Admin only.
 * `unlinkedFrom` names the record that was detached, so the caller can refresh
 * that record's documents.
 */
export async function deleteFax(faxId: string): Promise<{
  success: boolean;
  unlinkedFrom: FaxLinkRef | null;
}> {
  if (!faxId) {
    throw new Error('Fax ID is required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/faxes/${faxId}`, {
    method: 'DELETE',
  });
}

/** Restores a soft-deleted fax (the Undo for deleteFax). Admin only. */
export async function restoreFax(faxId: string): Promise<{
  success: boolean;
  relinkedTo: FaxLinkRef | null;
}> {
  if (!faxId) {
    throw new Error('Fax ID is required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/faxes/${faxId}/restore`, {
    method: 'POST',
  });
}

// ─── Inbound calls (voicemails / missed calls) — admin inbox ──────────────────

/** Lists inbound calls for the admin comms inbox. */
export async function fetchAdminInboundCalls(pagination?: {
  page?: number;
  limit?: number;
  type?: 'VOICEMAIL' | 'MISSED_CALL';
  status?: 'NEW' | 'IN_PROGRESS' | 'RESOLVED' | 'DISMISSED';
  search?: string;
}): Promise<PaginatedResponse<AdminInboundCallListItem>> {
  const params = new URLSearchParams();
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());
  if (pagination?.type) params.append('type', pagination.type);
  if (pagination?.status) params.append('status', pagination.status);
  if (pagination?.search) params.append('search', pagination.search);

  return fetchWithAuth<PaginatedResponse<AdminInboundCallListItem>>(
    `${API_BASE_URL}/admin/inbound-calls?${params.toString()}`
  );
}

/** Applies a triage update (status / assignee / notes) to an inbound call. */
export async function updateInboundCall(
  id: string,
  update: UpdateInboundCallPayload
): Promise<AdminInboundCallListItem> {
  if (!id) {
    throw new Error('Inbound call ID is required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/inbound-calls/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
}

/** Links an inbound call to a referral or records request. */
export async function linkInboundCall(
  id: string,
  recordId: string
): Promise<{ workflowType: 'REFERRAL' | 'MEDICAL_RECORD_REQUEST' }> {
  if (!id || !recordId) {
    throw new Error('Inbound call ID and record ID are required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/inbound-calls/${id}/link`, {
    method: 'POST',
    body: JSON.stringify({ recordId }),
  });
}

/** Unlinks an inbound call from its record. */
export async function unlinkInboundCall(
  id: string
): Promise<{ success: boolean }> {
  if (!id) {
    throw new Error('Inbound call ID is required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/inbound-calls/${id}/link`, {
    method: 'DELETE',
  });
}

/** Soft-deletes an inbound call (recoverable via restoreInboundCall). Admin only. */
export async function deleteInboundCall(
  id: string
): Promise<{ success: boolean }> {
  if (!id) {
    throw new Error('Inbound call ID is required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/inbound-calls/${id}`, {
    method: 'DELETE',
  });
}

/** Restores a soft-deleted inbound call (the Undo for deleteInboundCall). */
export async function restoreInboundCall(
  id: string
): Promise<{ success: boolean }> {
  if (!id) {
    throw new Error('Inbound call ID is required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/inbound-calls/${id}/restore`, {
    method: 'POST',
  });
}

/** Mints a fresh presigned URL for a voicemail recording (play or download). */
export async function generateInboundCallAudioUrl(
  id: string
): Promise<{ presignedUrl: string }> {
  if (!id) {
    throw new Error('Inbound call ID is required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/inbound-calls/${id}/audio-url`);
}

/** Fetches a single inbound call (used by the merged inbox preview). */
export async function fetchInboundCall(
  id: string
): Promise<AdminInboundCallListItem> {
  if (!id) {
    throw new Error('Inbound call ID is required.');
  }
  return fetchWithAuth(`${API_BASE_URL}/admin/inbound-calls/${id}`);
}

/** The merged "All" inbound inbox feed: faxes + voicemails + missed calls. */
export async function fetchInboxFeed(pagination?: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<PaginatedResponse<InboxFeedItem>> {
  const params = new URLSearchParams();
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());
  if (pagination?.search) params.append('search', pagination.search);

  return fetchWithAuth<PaginatedResponse<InboxFeedItem>>(
    `${API_BASE_URL}/admin/inbox?${params.toString()}`
  );
}

export async function getFaxById(faxId: string): Promise<FaxDetailResponse> {
  if (!faxId) {
    throw new Error('Fax ID is required.');
  }

  return fetchWithAuth<FaxDetailResponse>(`${API_BASE_URL}/faxes/${faxId}`);
}

export async function resendFax(faxId: string): Promise<SendFaxResponse> {
  if (!faxId) {
    throw new Error('Fax ID is required.');
  }

  return fetchWithAuth<SendFaxResponse>(
    `${API_BASE_URL}/faxes/${faxId}/resend`,
    {
      method: 'POST',
    }
  );
}

/**
 * Resend the most recent fax for a referral.
 * This endpoint finds the latest fax linked to the referral and creates a new fax.
 */
export async function resendReferralFax(
  referralId: string,
  faxNumberOverride?: string,
  // Which recipient to resend to. Required when >1 recipient is live (the
  // picker); omitted when only one is live (the server infers it).
  recipientId?: string
): Promise<SendFaxResponse> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }

  const body = {
    ...(faxNumberOverride ? { faxNumberOverride } : {}),
    ...(recipientId ? { recipientId } : {}),
  };

  return fetchWithAuth<SendFaxResponse>(
    `${API_BASE_URL}/referrals/${referralId}/resend`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

/**
 * Coordinator raises a "Needs your info" request on a referral (admin only). The
 * resulting alert is dismissed/resolved through the generic `resolveNotification`
 * endpoint, keyed by the notification id surfaced on the detail payload.
 */
export async function requestInfoFromClinic(
  referralId: string,
  body: { category?: string; message: string }
): Promise<{ success: true }> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }
  return fetchWithAuth<{ success: true }>(
    `${API_BASE_URL}/referrals/${referralId}/request-info`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

/** One document to include in an outgoing fax package. */
export interface FaxPackageDocumentSpec {
  documentId: string;
  /** 0-based page indices to keep; omit to send the document in full. */
  includedPages?: number[];
  /**
   * Total page count the selection was based on (pdf.js). Sent with
   * `includedPages` so the server can reject indices computed against a page
   * model that disagrees with the stored document.
   */
  pageCount?: number;
}

/**
 * Explicit fax package: which documents (and which pages) to send. Omit it to
 * fax every non-result document in full (the legacy behavior).
 */
export interface FaxPackageSpec {
  documents: FaxPackageDocumentSpec[];
}

export async function sendReferralDocuments(
  referralId: string,
  faxPackage?: FaxPackageSpec,
  // Which recipient to fax. Required when >1 recipient is live (the composer
  // picker); omitted when only one is live (the server infers it).
  recipientId?: string
): Promise<{ referralId: string; faxId: string }> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }

  const body = {
    ...(faxPackage ?? {}),
    ...(recipientId ? { recipientId } : {}),
  };
  const hasBody = Object.keys(body).length > 0;

  return fetchWithAuth<{ referralId: string; faxId: string }>(
    `${API_BASE_URL}/referrals/${referralId}/send-documents`,
    {
      method: 'POST',
      ...(hasBody
        ? {
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' },
          }
        : {}),
    }
  );
}

/**
 * Request results for a referral.
 * This sends a fax to the clinic requesting results for the patient.
 */
export async function requestReferralResults(
  referralId: string,
  // Which recipient to request results from. Required when >1 recipient is live
  // (the picker); omitted when only one is live (the server infers it).
  recipientId?: string
): Promise<{ faxId: string }> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }

  return fetchWithAuth<{ faxId: string }>(
    `${API_BASE_URL}/referrals/${referralId}/request-results`,
    {
      method: 'POST',
      ...(recipientId
        ? {
            body: JSON.stringify({ recipientId }),
            headers: { 'Content-Type': 'application/json' },
          }
        : {}),
    }
  );
}

/**
 * Cancel a referral (terminal). The referral moves to CANCELLED and any pending
 * provider e-signature link is expired server-side.
 */
export async function cancelReferral(
  referralId: string
): Promise<ReferralDetailResponse> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }

  return fetchWithAuth<ReferralDetailResponse>(
    `${API_BASE_URL}/referrals/${referralId}/cancel`,
    {
      method: 'POST',
    }
  );
}

/**
 * Mark a referral as Completed (a successful close-out the sender drives when a
 * referral concluded outside the system).
 */
export async function completeReferral(
  referralId: string
): Promise<ReferralDetailResponse> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }

  return fetchWithAuth<ReferralDetailResponse>(
    `${API_BASE_URL}/referrals/${referralId}/complete`,
    {
      method: 'POST',
    }
  );
}

/** Reopen a completed referral back to active (Awaiting Response). */
export async function reopenReferral(
  referralId: string
): Promise<ReferralDetailResponse> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }

  return fetchWithAuth<ReferralDetailResponse>(
    `${API_BASE_URL}/referrals/${referralId}/reopen`,
    {
      method: 'POST',
    }
  );
}

export async function upsertReferralAppointment(
  referralId: string,
  data: {
    // Clinic-local wall-clock; the server converts to a true UTC instant using
    // the recipient clinic's timezone. scheduledDate null clears the datetime.
    scheduledDate?: string | null;
    scheduledTime?: string | null;
    prepInstructions?: string | null;
    locationNotes?: string | null;
    /** Which live recipient clinic the appointment is at. */
    clinicId?: string;
    /**
     * Email the patient the time this save confirms (new or moved). Defaults
     * to true on the server; a save that keeps the time sends nothing.
     */
    notifyPatient?: boolean;
  }
): Promise<ReferralDetailResponse> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }
  return fetchWithAuth<ReferralDetailResponse>(
    `${API_BASE_URL}/referrals/${referralId}/appointment`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );
}

export async function getReferralMessages(
  referralId: string
): Promise<ReferralMessagesResponse> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }
  return fetchWithAuth<ReferralMessagesResponse>(
    `${API_BASE_URL}/referrals/${referralId}/messages`
  );
}

export async function sendReferralMessage(
  referralId: string,
  data: {
    channel: 'EMAIL' | 'SMS';
    subject?: string | null;
    body: string;
  }
): Promise<ReferralMessage> {
  if (!referralId) {
    throw new Error('Referral ID is required.');
  }
  return fetchWithAuth<ReferralMessage>(
    `${API_BASE_URL}/referrals/${referralId}/messages`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );
}

// --- Workflow Email Threads (Gmail integration) ---

export async function getReferralAutomatedEmails(
  referralId: string
): Promise<AutomatedEmail[]> {
  return fetchWithAuth<AutomatedEmail[]>(
    `${API_BASE_URL}/referrals/${referralId}/emails/automated`
  );
}

export async function getReferralAutomatedEmailContent(
  referralId: string,
  emailId: string
): Promise<AutomatedEmailContent> {
  return fetchWithAuth<AutomatedEmailContent>(
    `${API_BASE_URL}/referrals/${referralId}/emails/automated/${emailId}/content`
  );
}

export async function getReferralEmailUnreadCount(
  referralId: string
): Promise<EmailUnreadCount> {
  return fetchWithAuth<EmailUnreadCount>(
    `${API_BASE_URL}/referrals/${referralId}/emails/unread-count`
  );
}

export async function getReferralEmailThreads(
  referralId: string
): Promise<EmailThreadSummary[]> {
  return fetchWithAuth<EmailThreadSummary[]>(
    `${API_BASE_URL}/referrals/${referralId}/emails/threads`
  );
}

export async function getReferralEmailThread(
  referralId: string,
  threadId: string
): Promise<EmailThreadDetail> {
  return fetchWithAuth<EmailThreadDetail>(
    `${API_BASE_URL}/referrals/${referralId}/emails/threads/${threadId}`
  );
}

export async function markReferralEmailThreadRead(
  referralId: string,
  threadId: string,
  messageIds: string[]
): Promise<void> {
  return fetchWithAuth<void>(
    `${API_BASE_URL}/referrals/${referralId}/emails/threads/${threadId}/read`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messageIds }),
    }
  );
}

function fetchEmailAttachment(url: string): Promise<Blob> {
  return fetchWithAuth<Blob>(url, {
    readBody: response => response.blob(),
    timeoutMs: EMAIL_ATTACHMENT_TIMEOUT_MS,
  });
}

export async function getReferralEmailAttachment(
  referralId: string,
  threadId: string,
  messageId: string,
  index: number
): Promise<Blob> {
  return fetchEmailAttachment(
    `${API_BASE_URL}/referrals/${referralId}/emails/threads/${threadId}/messages/${messageId}/attachments/${index}`
  );
}

/** Adds a message's attachments to the referral's documents, each once. */
export async function saveReferralEmailAttachments(
  referralId: string,
  threadId: string,
  messageId: string,
  indexes: number[]
): Promise<{ ids: string[] }> {
  return fetchWithAuth<{ ids: string[] }>(
    `${API_BASE_URL}/referrals/${referralId}/emails/threads/${threadId}/messages/${messageId}/attachments/save`,
    {
      method: 'POST',
      body: JSON.stringify({ indexes }),
      timeoutMs: EMAIL_ATTACHMENT_TIMEOUT_MS,
    }
  );
}

export async function sendReferralEmail(
  referralId: string,
  data: SendEmailRequest
): Promise<EmailMessage> {
  return fetchWithAuth<EmailMessage>(
    `${API_BASE_URL}/referrals/${referralId}/emails/send`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );
}

export async function getRecordsRequestEmailUnreadCount(
  requestId: string
): Promise<EmailUnreadCount> {
  return fetchWithAuth<EmailUnreadCount>(
    `${API_BASE_URL}/records-requests/${requestId}/emails/unread-count`
  );
}

export async function getRecordsRequestEmailThreads(
  requestId: string
): Promise<EmailThreadSummary[]> {
  return fetchWithAuth<EmailThreadSummary[]>(
    `${API_BASE_URL}/records-requests/${requestId}/emails/threads`
  );
}

export async function getRecordsRequestEmailThread(
  requestId: string,
  threadId: string
): Promise<EmailThreadDetail> {
  return fetchWithAuth<EmailThreadDetail>(
    `${API_BASE_URL}/records-requests/${requestId}/emails/threads/${threadId}`
  );
}

export async function markRecordsRequestEmailThreadRead(
  requestId: string,
  threadId: string,
  messageIds: string[]
): Promise<void> {
  return fetchWithAuth<void>(
    `${API_BASE_URL}/records-requests/${requestId}/emails/threads/${threadId}/read`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messageIds }),
    }
  );
}

export async function getRecordsRequestEmailAttachment(
  requestId: string,
  threadId: string,
  messageId: string,
  index: number
): Promise<Blob> {
  return fetchEmailAttachment(
    `${API_BASE_URL}/records-requests/${requestId}/emails/threads/${threadId}/messages/${messageId}/attachments/${index}`
  );
}

export async function sendRecordsRequestEmail(
  requestId: string,
  data: SendEmailRequest
): Promise<EmailMessage> {
  return fetchWithAuth<EmailMessage>(
    `${API_BASE_URL}/records-requests/${requestId}/emails/send`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );
}

export async function completeProviderOnboarding(
  payload: CompleteProviderOnboardingPayload
): Promise<ProviderOnboardingResponse> {
  // Need credentials: 'include' to save the auth cookie after onboarding
  const response = await fetch(
    `${API_BASE_URL}/auth/onboarding/providers/complete`,
    {
      method: 'POST',
      credentials: 'include', // Important for setting auth cookie
      headers: {
        'Content-Type': 'application/json',
        // Authenticated wizard re-submits carry the auth cookie, so this raw
        // fetch (bypassing fetchWithAuth) must attach the CSRF header itself.
        [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || `Error: ${response.status}`;
    } catch {
      errorMessage = errorText || `Error: ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function completeMrrOnboarding(
  payload: CompleteMrrOnboardingPayload
): Promise<MrrOnboardingResponse> {
  const response = await fetch(`${API_BASE_URL}/auth/onboarding/mrr/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // Authenticated wizard re-submits carry the auth cookie, so this raw
      // fetch (bypassing fetchWithAuth) must attach the CSRF header itself.
      [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || `Error: ${response.status}`;
    } catch {
      errorMessage = errorText || `Error: ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function fetchPendingClinicInvitations(pagination?: {
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<PendingClinicInvitationResponseDto>> {
  const params = new URLSearchParams();
  if (pagination?.page) {
    params.append('page', pagination.page.toString());
  }
  if (pagination?.limit) {
    params.append('limit', pagination.limit.toString());
  }

  const queryString = params.toString();
  const url = `${API_BASE_URL}/invitations/pending-clinics${queryString ? `?${queryString}` : ''}`;

  return fetchWithAuth<PaginatedResponse<PendingClinicInvitationResponseDto>>(
    url
  );
}

export async function acceptInvitation(
  invitationId: string
): Promise<InvitationResponseDto> {
  if (!invitationId) {
    throw new Error('Invitation ID is required to accept an invitation.');
  }
  return fetchWithAuth(`${API_BASE_URL}/invitations/${invitationId}/accept`, {
    method: 'POST',
  });
}

export async function downloadReferralDocument(
  referralId: string
): Promise<void> {
  if (!referralId) {
    throw new Error('Referral ID is required to download document.');
  }

  const response = await fetch(
    `${API_BASE_URL}/referrals/${referralId}/document/download`,
    {
      credentials: 'include',
      headers: {
        Accept: '*/*',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || `Error: ${response.status}`;
    } catch {
      errorMessage = errorText || `Error: ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  const contentDisposition = response.headers.get('Content-Disposition');
  let finalFilename = 'referral_document';
  if (contentDisposition) {
    const headerFilename = contentDisposition
      .split('filename="')[1]
      ?.split('"')[0];
    if (headerFilename) {
      finalFilename = headerFilename;
    }
  }

  const blob = await response.blob();
  triggerBlobDownload(blob, finalFilename);
}

export async function downloadCombinedReferralDocument(
  referralId: string
): Promise<void> {
  if (!referralId) {
    throw new Error('Referral ID is required to download combined document.');
  }

  const response = await fetch(
    `${API_BASE_URL}/referrals/${referralId}/combined-download`,
    {
      credentials: 'include',
      headers: {
        Accept: 'application/pdf',
      },
    }
  );

  // Handle 204 No Content - indicates combined PDF not available
  if (response.status === 204) {
    throw new Error('COMBINED_PDF_NOT_AVAILABLE');
  }

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || `Error: ${response.status}`;
    } catch {
      errorMessage = errorText || `Error: ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  // The backend sets the filename in Content-Disposition header
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'Combined_Document.pdf';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) {
      filename = match[1];
    }
  }

  const blob = await response.blob();
  triggerBlobDownload(blob, filename);
}

export async function appendReferralDocument(
  referralId: string,
  tempKey: string
): Promise<void> {
  if (!referralId || !tempKey) {
    throw new Error('Referral ID and tempKey are required to append document.');
  }

  return fetchWithAuth(
    `${API_BASE_URL}/referrals/${referralId}/document/append`,
    {
      method: 'POST',
      body: JSON.stringify({ tempKey }),
    }
  );
}

export async function addWorkflowDocuments(
  referralId: string,
  s3Keys: string[]
): Promise<{ ids: string[] }> {
  if (!referralId || !s3Keys.length) {
    throw new Error('Referral ID and at least one S3 key are required.');
  }

  return fetchWithAuth(`${API_BASE_URL}/referrals/${referralId}/documents`, {
    method: 'POST',
    body: JSON.stringify({ s3Keys }),
  });
}

/**
 * Upload results for a referral.
 * Creates RESULT type documents, optionally marks referral as completed,
 * and sends notification emails to the sender clinic and patient.
 */
export async function uploadResults(
  referralId: string,
  s3Keys: string[],
  markCompleted = true,
  // Which recipient delivered. Required when >1 recipient is live (the picker);
  // omitted when only one is live (the server infers it).
  recipientId?: string
): Promise<{ ids: string[] }> {
  if (!referralId || !s3Keys.length) {
    throw new Error('Referral ID and at least one S3 key are required.');
  }

  return fetchWithAuth(`${API_BASE_URL}/referrals/${referralId}/results`, {
    method: 'POST',
    body: JSON.stringify({ s3Keys, markCompleted, recipientId }),
  });
}

export async function uploadAdditionalResults(
  referralId: string,
  s3Keys: string[]
): Promise<{ ids: string[] }> {
  if (!referralId || !s3Keys.length) {
    throw new Error('Referral ID and at least one S3 key are required.');
  }

  return fetchWithAuth(
    `${API_BASE_URL}/referrals/${referralId}/results/additional`,
    {
      method: 'POST',
      body: JSON.stringify({ s3Keys }),
    }
  );
}

/**
 * Shared implementation for the referral / records-request batch document
 * delete endpoints — they differ only in the resource path and the id label in
 * the validation message. `resourceLabel` keeps each caller's error wording.
 */
function batchDeleteDocumentsForWorkflow(
  resourcePath: 'referrals' | 'records-requests',
  resourceLabel: 'Referral' | 'Request',
  id: string,
  documentIds: string[]
): Promise<void> {
  if (!id || !documentIds.length) {
    throw new Error(
      `${resourceLabel} ID and at least one Document ID are required.`
    );
  }

  return fetchWithAuth(`${API_BASE_URL}/${resourcePath}/${id}/documents`, {
    method: 'DELETE',
    body: JSON.stringify({ documentIds }),
  });
}

export async function batchDeleteWorkflowDocuments(
  referralId: string,
  documentIds: string[]
): Promise<void> {
  return batchDeleteDocumentsForWorkflow(
    'referrals',
    'Referral',
    referralId,
    documentIds
  );
}

export async function downloadReferralFacesheet(
  referralId: string
): Promise<void> {
  if (!referralId) {
    throw new Error('Referral ID is required to download facesheet.');
  }

  const response = await fetch(
    `${API_BASE_URL}/referrals/${referralId}/facesheet`,
    {
      credentials: 'include',
      headers: {
        Accept: 'application/pdf',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || `Error: ${response.status}`;
    } catch {
      errorMessage = errorText || `Error: ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  // The backend sets the filename in Content-Disposition header
  const contentDisposition = response.headers.get('Content-Disposition');
  const filename = contentDisposition
    ? contentDisposition.split('filename="')[1]?.split('"')[0]
    : 'facesheet.pdf';

  const blob = await response.blob();
  triggerBlobDownload(blob, filename);
}

// Email Verification API
export async function verifyEmail(data: { code: string }): Promise<{
  statusCode: number;
  message: string;
  data: { verified: boolean };
}> {
  return fetchWithAuth<{
    statusCode: number;
    message: string;
    data: { verified: boolean };
  }>(`${API_BASE_URL}/email-verification/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
}

export async function sendVerificationCode(): Promise<{
  statusCode: number;
  message: string;
}> {
  return fetchWithAuth<{ statusCode: number; message: string }>(
    `${API_BASE_URL}/email-verification/send`,
    {
      method: 'POST',
    }
  );
}

export async function getVerificationStatus(): Promise<{
  statusCode: number;
  data: {
    hasActiveVerification: boolean;
    isVerified: boolean;
    canResend: boolean;
    attemptsRemaining: number;
    secondsUntilCanResend: number;
    context?: any;
  };
}> {
  return fetchWithAuth<{
    statusCode: number;
    data: {
      hasActiveVerification: boolean;
      isVerified: boolean;
      canResend: boolean;
      attemptsRemaining: number;
      secondsUntilCanResend: number;
    };
  }>(`${API_BASE_URL}/email-verification/status`);
}

// Phone Calls
/** Calls for one referral (`referralId`) or the recent-calls review list. */
export async function listPhoneCalls(
  query: PhoneCallsQuery
): Promise<PhoneCallListItem[]> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.append(key, String(value));
  }
  const suffix = params.toString() ? `?${params}` : '';
  return fetchWithAuth<PhoneCallListItem[]>(
    `${API_BASE_URL}/voice/phone-calls${suffix}`
  );
}

export async function getPhoneCallRecordingUrl(
  id: string
): Promise<{ presignedUrl: string }> {
  return fetchWithAuth(`${API_BASE_URL}/voice/phone-calls/${id}/recording-url`);
}

export async function retriggerPhoneCall(
  id: string,
  payload?: RetriggerPhoneCallPayload
): Promise<RetriggerPhoneCallResponse> {
  return fetchWithAuth<RetriggerPhoneCallResponse>(
    `${API_BASE_URL}/voice/phone-calls/${id}/retrigger`,
    {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    }
  );
}

// Referral Documents API
export async function getReferralDocuments(
  referralId: string
): Promise<ReferralDocumentsResponse> {
  if (!referralId) {
    throw new Error('Referral ID is required to fetch documents.');
  }

  return fetchWithAuth<ReferralDocumentsResponse>(
    `${API_BASE_URL}/referrals/${referralId}/documents`
  );
}

export async function downloadReferralWorkflowDocument(
  referralId: string,
  documentId: string
): Promise<void> {
  if (!referralId || !documentId) {
    throw new Error('Referral ID and Document ID are required to download.');
  }

  const response = await fetch(
    `${API_BASE_URL}/referrals/${referralId}/documents/${documentId}/download`,
    {
      credentials: 'include',
      headers: {
        Accept: '*/*',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || `Error: ${response.status}`;
    } catch {
      errorMessage = errorText || `Error: ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'document.pdf';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) {
      filename = match[1];
    }
  }

  const blob = await response.blob();
  triggerBlobDownload(blob, filename);
}

// Workflow Notes API (polymorphic: referrals + records requests)
export async function getWorkflowNotes(
  workflowType: WorkflowType,
  workflowId: string
): Promise<WorkflowNotesResponse> {
  if (!workflowId) {
    throw new Error('Workflow ID is required to fetch notes.');
  }

  return fetchWithAuth<WorkflowNotesResponse>(
    `${API_BASE_URL}/workflows/${workflowType}/${workflowId}/notes`
  );
}

export async function createWorkflowNote(
  workflowType: WorkflowType,
  workflowId: string,
  content: string
): Promise<WorkflowNote> {
  if (!workflowId || !content) {
    throw new Error('Workflow ID and content are required to create a note.');
  }

  return fetchWithAuth<WorkflowNote>(
    `${API_BASE_URL}/workflows/${workflowType}/${workflowId}/notes`,
    {
      method: 'POST',
      body: JSON.stringify({ content }),
    }
  );
}

export async function updateWorkflowNote(
  workflowType: WorkflowType,
  workflowId: string,
  noteId: string,
  content: string
): Promise<WorkflowNote> {
  if (!workflowId || !noteId || !content) {
    throw new Error(
      'Workflow ID, Note ID, and content are required to update a note.'
    );
  }

  return fetchWithAuth<WorkflowNote>(
    `${API_BASE_URL}/workflows/${workflowType}/${workflowId}/notes/${noteId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }
  );
}

export async function deleteWorkflowNote(
  workflowType: WorkflowType,
  workflowId: string,
  noteId: string
): Promise<void> {
  if (!workflowId || !noteId) {
    throw new Error('Workflow ID and Note ID are required to delete a note.');
  }

  return fetchWithAuth<void>(
    `${API_BASE_URL}/workflows/${workflowType}/${workflowId}/notes/${noteId}`,
    {
      method: 'DELETE',
    }
  );
}

// Workflow Messages API (shared admin <-> submitter thread)
export async function getWorkflowMessages(
  workflowType: WorkflowType,
  workflowId: string
): Promise<WorkflowMessagesResponse> {
  if (!workflowId) {
    throw new Error('Workflow ID is required to fetch messages.');
  }

  return fetchWithAuth<WorkflowMessagesResponse>(
    `${API_BASE_URL}/workflows/${workflowType}/${workflowId}/messages`
  );
}

export async function sendWorkflowMessage(
  workflowType: WorkflowType,
  workflowId: string,
  body: string
): Promise<WorkflowMessage> {
  if (!workflowId || !body) {
    throw new Error('Workflow ID and message body are required to send.');
  }

  return fetchWithAuth<WorkflowMessage>(
    `${API_BASE_URL}/workflows/${workflowType}/${workflowId}/messages`,
    {
      method: 'POST',
      body: JSON.stringify({ body }),
    }
  );
}

export async function markWorkflowMessagesRead(
  workflowType: WorkflowType,
  workflowId: string
): Promise<void> {
  if (!workflowId) {
    throw new Error('Workflow ID is required to mark messages read.');
  }

  return fetchWithAuth<void>(
    `${API_BASE_URL}/workflows/${workflowType}/${workflowId}/messages/read`,
    {
      method: 'POST',
    }
  );
}

// ============ Clinic Favorites API ============

/**
 * Get favorite clinics for a sender clinic.
 * Returns clinics that the sender clinic has marked as favorites.
 */
export async function getFavoriteClinics(
  clinicId: string
): Promise<{ data: ClinicInfo[] }> {
  if (!clinicId) {
    throw new Error('Clinic ID is required to fetch favorites.');
  }

  return fetchWithAuth<{ data: ClinicInfo[] }>(
    `${API_BASE_URL}/clinics/${clinicId}/favorites`
  );
}

/**
 * Add a clinic to favorites.
 */
export async function addFavoriteClinic(
  clinicId: string,
  favoriteClinicId: string
): Promise<void> {
  if (!clinicId || !favoriteClinicId) {
    throw new Error(
      'Clinic ID and Favorite Clinic ID are required to add a favorite.'
    );
  }

  return fetchWithAuth<void>(
    `${API_BASE_URL}/clinics/${clinicId}/favorites/${favoriteClinicId}`,
    {
      method: 'POST',
    }
  );
}

/**
 * Remove a clinic from favorites.
 */
export async function removeFavoriteClinic(
  clinicId: string,
  favoriteClinicId: string
): Promise<void> {
  if (!clinicId || !favoriteClinicId) {
    throw new Error(
      'Clinic ID and Favorite Clinic ID are required to remove a favorite.'
    );
  }

  return fetchWithAuth<void>(
    `${API_BASE_URL}/clinics/${clinicId}/favorites/${favoriteClinicId}`,
    {
      method: 'DELETE',
    }
  );
}

// ── Patients ──

export async function fetchPatients(
  clinicId: string,
  options?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    name?: string;
    dateOfBirth?: string;
    phoneNumber?: string;
    email?: string;
    recentActivity?: boolean;
  }
): Promise<PaginatedResponse<PatientListItem>> {
  if (!clinicId) throw new Error('Clinic ID is required');

  const params = new URLSearchParams();
  if (options?.page) params.append('page', options.page.toString());
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.sortBy) params.append('sortBy', options.sortBy);
  if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
  if (options?.name) params.append('name', options.name);
  if (options?.dateOfBirth) params.append('dateOfBirth', options.dateOfBirth);
  if (options?.phoneNumber) params.append('phoneNumber', options.phoneNumber);
  if (options?.email) params.append('email', options.email);
  if (options?.recentActivity)
    params.append('recentActivity', String(options.recentActivity));

  const queryString = params.toString() ? `?${params.toString()}` : '';
  return fetchWithAuth<PaginatedResponse<PatientListItem>>(
    `${API_BASE_URL}/clinics/${clinicId}/patients${queryString}`
  );
}

export async function fetchPatientById(
  clinicId: string,
  patientId: string
): Promise<PatientDetailResponse> {
  if (!clinicId || !patientId)
    throw new Error('Clinic ID and Patient ID are required');

  return fetchWithAuth<PatientDetailResponse>(
    `${API_BASE_URL}/clinics/${clinicId}/patients/${patientId}`
  );
}

export async function createPatient(
  clinicId: string,
  data: CreatePatientPayload
): Promise<PatientDetailResponse> {
  if (!clinicId) throw new Error('Clinic ID is required');

  return fetchWithAuth<PatientDetailResponse>(
    `${API_BASE_URL}/clinics/${clinicId}/patients`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );
}

export async function updatePatient(
  clinicId: string,
  patientId: string,
  data: UpdatePatientPayload
): Promise<PatientDetailResponse> {
  if (!clinicId || !patientId)
    throw new Error('Clinic ID and Patient ID are required');

  return fetchWithAuth<PatientDetailResponse>(
    `${API_BASE_URL}/clinics/${clinicId}/patients/${patientId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );
}

export async function invitePatientToPlatform(
  clinicId: string,
  patientId: string
): Promise<void> {
  if (!clinicId || !patientId)
    throw new Error('Clinic ID and Patient ID are required');

  return fetchWithAuth<void>(
    `${API_BASE_URL}/clinics/${clinicId}/patients/${patientId}/invite`,
    {
      method: 'POST',
    }
  );
}

// ===== Appointments =====

export async function fetchAppointments(params?: {
  page?: number;
  limit?: number;
  status?: string;
  source?: string;
}): Promise<PaginatedResponse<AppointmentListItem>> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.source) searchParams.set('source', params.source);

  const query = searchParams.toString();
  const url = `${API_BASE_URL}/appointments${query ? `?${query}` : ''}`;
  return fetchWithAuth(url);
}

export async function fetchAppointmentById(
  id: string
): Promise<AppointmentDetailResponse> {
  return fetchWithAuth(`${API_BASE_URL}/appointments/${id}`);
}

export async function confirmAppointment(
  id: string
): Promise<AppointmentDetailResponse> {
  return fetchWithAuth(`${API_BASE_URL}/appointments/${id}/confirm`, {
    method: 'POST',
  });
}

export async function rejectAppointment(
  id: string,
  rejectionReason: string
): Promise<AppointmentDetailResponse> {
  return fetchWithAuth(`${API_BASE_URL}/appointments/${id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rejectionReason }),
  });
}

export async function updateAppointment(
  id: string,
  data: {
    // Clinic-local wall-clock; converted server-side using the appointment's
    // stored timezone (or the linked clinic's).
    scheduledDate?: string;
    scheduledTime?: string | null;
    callSummary?: string;
    prepInstructions?: string | null;
    parkingInstructions?: string | null;
    screeningQuestions?: string | null;
  }
): Promise<AppointmentDetailResponse> {
  return fetchWithAuth(`${API_BASE_URL}/appointments/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function linkAppointmentReferral(
  id: string,
  referralId: string
): Promise<AppointmentDetailResponse> {
  return fetchWithAuth(`${API_BASE_URL}/appointments/${id}/link-referral`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ referralId }),
  });
}

// --- Billing ---

export async function getCredits(clinicId: string): Promise<CreditsResponse> {
  return fetchWithAuth<CreditsResponse>(
    `${API_BASE_URL}/billing/credits?clinicId=${encodeURIComponent(clinicId)}`
  );
}

export async function savePaymentMethod(
  payload: SavePaymentMethodPayload
): Promise<CreditsResponse> {
  return fetchWithAuth<CreditsResponse>(
    `${API_BASE_URL}/billing/payment-method`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

// --- Records Requests ---

export interface CreateRecordsRequestPayload {
  clinicId?: string;
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  patientPhone?: string;
  patientEmail?: string;
  patientSsn?: string;
  patientMrn?: string;
  recipientClinicId?: string;
  recipientFaxNumber?: string;
  recipientName?: string;
  recordsType: string[];
  dateRangeStart?: string;
  dateRangeEnd?: string;
  additionalNotes?: string;
  caseReference?: string;
  authorizationMethod: 'UPLOADED' | 'GENERATED';
  uploadedFiles: { s3Key: string }[];
}

export interface CreateRecordsRequestResponse {
  id: string;
  // null when the recipient clinic requires portal submission (no fax is sent).
  faxId: string | null;
  status: string;
  recipientFaxNumber: string | null;
  recipientName: string | null;
  createdAt: string;
}

export async function createRecordsRequest(
  payload: CreateRecordsRequestPayload
): Promise<CreateRecordsRequestResponse> {
  return fetchWithAuth<CreateRecordsRequestResponse>(
    `${API_BASE_URL}/records-requests`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export interface GenerateAuthorizationPayload {
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  patientSsn: string;
  patientMrn?: string;
  patientAddress: { street: string; city: string; state: string; zip: string };
  clinicName: string;
  clinicAddress?: string;
  clinicFax?: string;
  recipientClinicId?: string;
  recipientName: string;
  recipientAddress?: string;
  recordsToRelease: string[];
  purposeOfRelease: string;
  authorizationExpiration: string;
}

export interface GenerateAuthorizationResponse {
  s3Key: string;
  s3Url: string;
  pdfBlob: Blob;
}

export async function generateAuthorization(
  payload: GenerateAuthorizationPayload
): Promise<GenerateAuthorizationResponse> {
  const makeRequest = () =>
    fetch(`${API_BASE_URL}/records-requests/generate-authorization`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
      },
      body: JSON.stringify(payload),
    });

  let response = await makeRequest();

  if (response.status === 401) {
    const refresh = await attemptTokenRefresh();
    if (refresh === 'refreshed') {
      response = await makeRequest();
    } else {
      const endpoint = `${API_BASE_URL}/records-requests/generate-authorization`;
      handleSessionExpired();
      throw refresh === 'rejected'
        ? new SessionExpiredError(endpoint, 'POST')
        : new ApiError(REFRESH_UNAVAILABLE_MESSAGE, endpoint, 'POST', 401);
    }
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to generate authorization');
  }

  const s3Key = response.headers.get('X-S3-Key') ?? '';
  const s3Url = response.headers.get('X-S3-Url') ?? '';
  const pdfBlob = await response.blob();

  return { s3Key, s3Url, pdfBlob };
}

// E-Signature types
export interface ESignatureVerifyResponse {
  workflowType: 'MEDICAL_RECORD_REQUEST' | 'REFERRAL';
  patientFirstName: string;
  patientLastName: string;
  clinicName?: string;
  recordsToRelease?: string[];
  recipientName?: string;
  authorizationExpiration?: string;
  providerName?: string;
  providerNpi?: string;
  orderDescription?: string;
  eSignatureExpiresAt: string;
  authorizationText: string;
}

export interface SendESignaturePayload {
  clinicId?: string;
  patientEmail: string;
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  patientPhone?: string;
  patientSsn?: string;
  patientMrn?: string;
  patientAddress: { street: string; city: string; state: string; zip: string };
  recipientClinicId?: string;
  recipientFaxNumber?: string;
  recipientName?: string;
  recordsType: string[];
  dateRangeStart?: string;
  dateRangeEnd?: string;
  additionalNotes?: string;
  caseReference?: string;
  clinicName: string;
  clinicAddress?: string;
  clinicFax?: string;
  recipientNameForAuth: string;
  recipientAddress?: string;
  recordsToRelease: string[];
  purposeOfRelease: string;
  authorizationExpiration: string;
}

export interface SendESignatureResponse {
  id: string;
  status: string;
}

// Public — no auth cookie needed
export async function verifyESignatureToken(
  token: string
): Promise<ESignatureVerifyResponse> {
  const response = await fetch(`${API_BASE_URL}/e-signature/verify/${token}`);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Invalid or expired signing link');
  }
  return response.json();
}

export async function submitESignature(
  token: string,
  data: { signerName: string; consentChecked: boolean }
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/e-signature/sign/${token}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to submit signature');
  }
}

// ── Patient scheduling follow-up (public, token-gated) ──────────────────────

export interface SchedulingFollowUpAnswer {
  questionKey: string;
  value: unknown;
}

export interface SchedulingFollowUpQuestion {
  wording: string;
  stage: 'details' | 'experience';
  /** Clinic-outreach branch this question belongs to; null = both branches. */
  branch: boolean | null;
  /** Gate on the IS_SCHEDULED answer; undefined = valid either way. */
  requiresScheduled?: boolean;
  /** v2+: valid only while the IS_SCHEDULED choiceKey is one of these. */
  requiresScheduledChoice?: string[];
  /**
   * v4+: true = only once the patient has been in contact with the clinic
   * (it reached out, or they have an appointment); false = only when neither.
   */
  requiresClinicContact?: boolean;
  type: 'boolean' | 'choice' | 'numeric' | 'text' | 'date' | 'time';
  options?: { key: string; label: string }[];
  allowsText?: boolean;
  /** Date questions only: the date cannot be after today. */
  pastOnly?: boolean;
  min?: number;
  max?: number;
  labels?: string[];
}

export interface SchedulingFollowUpVerifyResponse {
  patientFirstName: string | null;
  clinicName: string | null;
  procedure: string | null;
  /** `YYYY-MM-DD` in the clinic's timezone — the day the request was submitted. */
  requestedOn: string | null;
  /** The top-level answer: did the clinic reach out? null = unanswered. */
  clinicReachedOut: boolean | null;
  respondedAt: string | null;
  questionSetVersion: number;
  questions: Record<string, SchedulingFollowUpQuestion>;
  answers: SchedulingFollowUpAnswer[];
}

// Public — no auth cookie needed. Read-only: answers are only recorded by the
// POSTs below (email scanners prefetch the emailed links via GET).
export async function verifySchedulingFollowUp(
  token: string
): Promise<SchedulingFollowUpVerifyResponse> {
  const response = await fetch(
    `${API_BASE_URL}/patient-followups/verify/${token}`
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Invalid or expired follow-up link');
  }
  return response.json();
}

/** Where the answer came from; the API only scanner-guards `EMAIL_LINK`. */
export type SchedulingFollowUpResponseSource = 'EMAIL_LINK' | 'PAGE';

export interface SchedulingFollowUpRespondResult {
  success: boolean;
  /** False when the API discarded the answer as an email-scanner artifact. */
  recorded: boolean;
}

export async function respondSchedulingFollowUp(
  token: string,
  data: {
    clinicReachedOut: boolean;
    source: SchedulingFollowUpResponseSource;
  }
): Promise<SchedulingFollowUpRespondResult> {
  const response = await fetch(
    `${API_BASE_URL}/patient-followups/respond/${token}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
      },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to record your answer');
  }
  return response.json();
}

export async function submitSchedulingFollowUpAnswers(
  token: string,
  data: {
    answers: {
      questionKey: string;
      value: unknown;
    }[];
  }
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/patient-followups/answers/${token}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
      },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to submit your answers');
  }
}

// ── Follow-up results (admin) ───────────────────────────────────────────────

export type AdminFollowUpStatus = 'PENDING' | 'SENT' | 'RESPONDED' | 'EXPIRED';

export interface AdminReferralFollowUp {
  id: string;
  status: AdminFollowUpStatus;
  sentAt: string | null;
  reminderSentAt: string | null;
  respondedAt: string | null;
  expiresAt: string;
  respondedVia: SchedulingFollowUpResponseSource | null;
  /** Responded via the emailed one-tap link within 60s of send — may be an
   *  email-security scanner rather than the patient. */
  scannerSuspect: boolean;
  /** When set, a suspected scanner response was discarded at this time. */
  scannerSuspectedAt: string | null;
  clinicReachedOut: boolean | null;
  questionSetVersion: number;
  /** Registry the patient answered under; empty for unknown future versions. */
  questions: Record<string, SchedulingFollowUpQuestion>;
  answers: SchedulingFollowUpAnswer[];
  /** Answer values scrubbed by patient anonymization. */
  anonymized: boolean;
}

export interface AdminReferralFollowUpResponse {
  followUp: AdminReferralFollowUp | null;
}

export async function getAdminReferralFollowUp(
  referralId: string
): Promise<AdminReferralFollowUpResponse> {
  return fetchWithAuth(
    `${API_BASE_URL}/patient-followups/admin/referral/${referralId}`
  );
}

// ── Patient visit follow-up (public, token-gated) ───────────────────────────
// The post-visit "were you seen?" survey — same architecture as the scheduling
// follow-up above and shares its question/answer/source types.

export interface VisitFollowUpVerifyResponse {
  patientFirstName: string | null;
  clinicName: string | null;
  procedure: string | null;
  /** The top-level answer: was the patient seen? null = unanswered. */
  wasSeen: boolean | null;
  respondedAt: string | null;
  questionSetVersion: number;
  questions: Record<string, SchedulingFollowUpQuestion>;
  answers: SchedulingFollowUpAnswer[];
}

// Public — no auth cookie needed. Read-only: answers are only recorded by the
// POSTs below (email scanners prefetch the emailed links via GET).
export async function verifyVisitFollowUp(
  token: string
): Promise<VisitFollowUpVerifyResponse> {
  const response = await fetch(
    `${API_BASE_URL}/visit-followups/verify/${token}`
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Invalid or expired follow-up link');
  }
  return response.json();
}

export async function respondVisitFollowUp(
  token: string,
  data: {
    wasSeen: boolean;
    source: SchedulingFollowUpResponseSource;
  }
): Promise<SchedulingFollowUpRespondResult> {
  const response = await fetch(
    `${API_BASE_URL}/visit-followups/respond/${token}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
      },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to record your answer');
  }
  return response.json();
}

export async function submitVisitFollowUpAnswers(
  token: string,
  data: {
    answers: {
      questionKey: string;
      value: unknown;
    }[];
  }
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/visit-followups/answers/${token}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
      },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to submit your answers');
  }
}

// ── Visit follow-up results (admin) ─────────────────────────────────────────

export interface AdminReferralVisitFollowUp {
  id: string;
  status: AdminFollowUpStatus;
  sentAt: string | null;
  reminderSentAt: string | null;
  respondedAt: string | null;
  expiresAt: string;
  respondedVia: SchedulingFollowUpResponseSource | null;
  /** Responded via the emailed one-tap link within 60s of send — may be an
   *  email-security scanner rather than the patient. */
  scannerSuspect: boolean;
  /** When set, a suspected scanner response was discarded at this time. */
  scannerSuspectedAt: string | null;
  wasSeen: boolean | null;
  questionSetVersion: number;
  /** Registry the patient answered under; empty for unknown future versions. */
  questions: Record<string, SchedulingFollowUpQuestion>;
  answers: SchedulingFollowUpAnswer[];
  /** Answer values scrubbed by patient anonymization. */
  anonymized: boolean;
}

export interface AdminReferralVisitFollowUpResponse {
  followUp: AdminReferralVisitFollowUp | null;
}

export async function getAdminReferralVisitFollowUp(
  referralId: string
): Promise<AdminReferralVisitFollowUpResponse> {
  return fetchWithAuth(
    `${API_BASE_URL}/visit-followups/admin/referral/${referralId}`
  );
}

// Authenticated
export async function sendESignatureRequest(
  data: SendESignaturePayload
): Promise<SendESignatureResponse> {
  return fetchWithAuth<SendESignatureResponse>(
    `${API_BASE_URL}/e-signature/send`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );
}

export async function resendESignatureRequest(id: string): Promise<void> {
  await fetchWithAuth(`${API_BASE_URL}/e-signature/${id}/resend`, {
    method: 'POST',
  });
}

export interface RecordsRequestListItem {
  id: string;
  status: string;
  internalStage: string | null;
  recipientName: string | null;
  recipientFaxNumber: string | null;
  recordsType: string[];
  additionalNotes: string | null;
  createdAt: string;
  updatedAt: string;
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  };
  createdByUser: {
    id: string;
    firstName: string;
    lastName: string;
  };
  recipientClinic: {
    id: string;
    name: string;
    faxNumber: string;
    phone: string;
    recordsRequestSubmissionMethod?: RecordsRequestSubmissionMethod | null;
  } | null;
  senderClinic?: {
    id: string;
    name: string;
  } | null;
  faxes: {
    id: string;
    status: string;
    createdAt: string;
  }[];
  coordinatorTab?: string | null;
  lastFaxError?: {
    errorMessage: string | null;
    faxNumber: string | null;
    failedAt: string;
  } | null;
  hasUnreadMessages?: boolean;
}

export async function fetchRecordsRequests(
  clinicId: string
): Promise<RecordsRequestListItem[]> {
  return fetchWithAuth<RecordsRequestListItem[]>(
    `${API_BASE_URL}/records-requests?clinicId=${clinicId}`
  );
}

type AdminRecordsRequestsResponse =
  PaginatedResponse<RecordsRequestListItem> & {
    statusCounts?: Record<string, number>;
  };

export async function fetchAdminRecordsRequests(params?: {
  page?: number;
  limit?: number;
  status?: string;
  recordsType?: string;
  clinicId?: string;
  anyClinicId?: string;
  coordinatorTab?: string;
}): Promise<AdminRecordsRequestsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.limit) searchParams.append('limit', params.limit.toString());
  if (params?.status) searchParams.append('status', params.status);
  if (params?.recordsType)
    searchParams.append('recordsType', params.recordsType);
  if (params?.clinicId) searchParams.append('clinicId', params.clinicId);
  if (params?.anyClinicId)
    searchParams.append('anyClinicId', params.anyClinicId);
  if (params?.coordinatorTab)
    searchParams.append('coordinatorTab', params.coordinatorTab);
  const queryString = searchParams.toString();
  return fetchWithAuth<AdminRecordsRequestsResponse>(
    `${API_BASE_URL}/records-requests/admin${queryString ? `?${queryString}` : ''}`
  );
}

// --- Records Request Detail API ---

import type {
  RecordsRequestDetail,
  RecordsRequestDocumentsResponse,
} from '@/types/records-requests';

export async function fetchRecordsRequestById(
  id: string
): Promise<RecordsRequestDetail> {
  return fetchWithAuth<RecordsRequestDetail>(
    `${API_BASE_URL}/records-requests/${id}`
  );
}

export async function fetchRecordsRequestDocuments(
  id: string
): Promise<RecordsRequestDocumentsResponse> {
  return fetchWithAuth<RecordsRequestDocumentsResponse>(
    `${API_BASE_URL}/records-requests/${id}/documents`
  );
}

export async function resendRecordsRequestFax(
  id: string
): Promise<{ faxId: string }> {
  return fetchWithAuth<{ faxId: string }>(
    `${API_BASE_URL}/records-requests/${id}/resend`,
    { method: 'POST' }
  );
}

/**
 * Fax a composed package of the request's documents (and, per document, a
 * chosen subset of pages) to the recipient clinic. Omit the package to send
 * every non-result document in full. A fresh cover page is prepended
 * server-side.
 */
export async function sendRecordsRequestDocuments(
  id: string,
  faxPackage?: FaxPackageSpec
): Promise<{ faxId: string }> {
  return fetchWithAuth<{ faxId: string }>(
    `${API_BASE_URL}/records-requests/${id}/send-documents`,
    {
      method: 'POST',
      ...(faxPackage
        ? {
            body: JSON.stringify(faxPackage),
            headers: { 'Content-Type': 'application/json' },
          }
        : {}),
    }
  );
}

export async function updateRecordsRequestPatientDetails(
  id: string,
  updates: {
    firstName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
  }
): Promise<RecordsRequestDetail> {
  return fetchWithAuth<RecordsRequestDetail>(
    `${API_BASE_URL}/records-requests/${id}/patient`,
    {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }
  );
}

export async function addRecordsRequestDocuments(
  id: string,
  s3Keys: string[]
): Promise<{ success: boolean; documentCount: number }> {
  return fetchWithAuth<{ success: boolean; documentCount: number }>(
    `${API_BASE_URL}/records-requests/${id}/add-documents`,
    {
      method: 'POST',
      body: JSON.stringify({
        uploadedFiles: s3Keys.map(s3Key => ({ s3Key })),
      }),
    }
  );
}

export async function batchDeleteRecordsRequestDocuments(
  requestId: string,
  documentIds: string[]
): Promise<void> {
  return batchDeleteDocumentsForWorkflow(
    'records-requests',
    'Request',
    requestId,
    documentIds
  );
}

export async function uploadRecordsRequestResults(
  id: string,
  s3Keys: string[]
): Promise<{ success: boolean; documentCount: number }> {
  return fetchWithAuth<{ success: boolean; documentCount: number }>(
    `${API_BASE_URL}/records-requests/${id}/upload-results`,
    {
      method: 'POST',
      body: JSON.stringify({
        uploadedFiles: s3Keys.map(s3Key => ({ s3Key })),
      }),
    }
  );
}

export async function addRecordsRequestNote(
  id: string,
  content: string
): Promise<{ success: boolean }> {
  return fetchWithAuth<{ success: boolean }>(
    `${API_BASE_URL}/records-requests/${id}/notes`,
    {
      method: 'POST',
      body: JSON.stringify({ content }),
    }
  );
}

export async function updateRecordsRequestStatus(
  id: string,
  status: string
): Promise<void> {
  await fetchWithAuth<void>(`${API_BASE_URL}/records-requests/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function downloadRecordsRequestDocument(
  requestId: string,
  documentId: string
): Promise<void> {
  if (!requestId || !documentId) {
    throw new Error('Request ID and Document ID are required to download.');
  }

  const response = await fetch(
    `${API_BASE_URL}/records-requests/${requestId}/documents/${documentId}/download`,
    {
      credentials: 'include',
      headers: {
        Accept: '*/*',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage;
    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || `Error: ${response.status}`;
    } catch {
      errorMessage = errorText || `Error: ${response.status}`;
    }
    throw new Error(errorMessage);
  }

  const contentDisposition = response.headers.get('Content-Disposition');
  const blob = await response.blob();

  let filename: string | undefined;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) {
      filename = match[1];
    }
  }
  if (!filename) {
    const mimeToExt: Record<string, string> = {
      'application/pdf': '.pdf',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/tiff': '.tiff',
    };
    const ext = mimeToExt[blob.type] || '';
    filename = `document${ext}`;
  }

  triggerBlobDownload(blob, filename);
}

// ===== Admin patient search =====

export async function fetchAdminPatientSearch(params: {
  name?: string;
  dateOfBirth?: string;
  phoneNumber?: string;
  email?: string;
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<AdminPatientSearchResult>> {
  const searchParams = new URLSearchParams();
  if (params.name) searchParams.set('name', params.name);
  if (params.dateOfBirth) searchParams.set('dateOfBirth', params.dateOfBirth);
  if (params.phoneNumber) searchParams.set('phoneNumber', params.phoneNumber);
  if (params.email) searchParams.set('email', params.email);
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());

  const query = searchParams.toString();
  const url = `${API_BASE_URL}/admin/patients${query ? `?${query}` : ''}`;
  return fetchWithAuth<PaginatedResponse<AdminPatientSearchResult>>(url);
}

export async function fetchAdminPatientProfile(
  patientId: string
): Promise<AdminPatientProfile> {
  return fetchWithAuth<AdminPatientProfile>(
    `${API_BASE_URL}/admin/patients/${patientId}`
  );
}

/**
 * Admin: delete a patient's data in both stores — WordPress close/scrub
 * (synchronous, best-effort, reported per email) + RMP anonymization
 * (async; the receipt id tracks it).
 */
export async function adminAnonymizePatient(
  patientId: string
): Promise<AdminAnonymizePatientResponse> {
  return fetchWithAuth<AdminAnonymizePatientResponse>(
    `${API_BASE_URL}/admin/patients/${patientId}/anonymize`,
    { method: 'POST', timeoutMs: ADMIN_ANONYMIZE_TIMEOUT_MS }
  );
}

// Workflow admin controls used by All Referrals/detail pages.
export async function updateInternalStage(
  workflowType: string,
  id: string,
  internalStage: string
) {
  return fetchWithAuth<{ success: boolean }>(
    `${API_BASE_URL}/coordinator-inbox/${workflowType}/${id}/internal-stage`,
    {
      method: 'PATCH',
      body: JSON.stringify({ internalStage }),
    }
  );
}

export async function updateCoordinatorTab(
  workflowType: string,
  id: string,
  tab: string
) {
  return fetchWithAuth<{ success: boolean }>(
    `${API_BASE_URL}/coordinator-inbox/${workflowType}/${id}/coordinator-tab`,
    {
      method: 'PATCH',
      body: JSON.stringify({ tab }),
    }
  );
}

// ---------------------------------------------------------------------------
// Notifications (coordinator notification center)
// ---------------------------------------------------------------------------

export async function getNotifications(params?: {
  status?: 'open' | 'resolved';
  subjectType?: SubjectType;
  subjectId?: string;
  type?: string;
  limit?: number;
  cursor?: string;
}): Promise<NotificationPage> {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.subjectType) search.set('subjectType', params.subjectType);
  if (params?.subjectId) search.set('subjectId', params.subjectId);
  if (params?.type) search.set('type', params.type);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.cursor) search.set('cursor', params.cursor);
  const qs = search.toString();
  return fetchWithAuth<NotificationPage>(
    `${API_BASE_URL}/notifications${qs ? `?${qs}` : ''}`
  );
}

export async function getNotificationCount(): Promise<NotificationCounts> {
  return fetchWithAuth<NotificationCounts>(
    `${API_BASE_URL}/notifications/count`
  );
}

export async function getNotificationCounts(
  subjectType: SubjectType,
  subjectIds: string[]
): Promise<Record<string, SubjectOpenCount>> {
  return fetchWithAuth<Record<string, SubjectOpenCount>>(
    `${API_BASE_URL}/notifications/counts`,
    {
      method: 'POST',
      body: JSON.stringify({ subjectType, subjectIds }),
    }
  );
}

/**
 * Resolve a single notification by id — the generic, audience-agnostic dismiss.
 * The API authorizes against the notification row (an admin may resolve any; a
 * clinic user may resolve a CLINIC alert addressed to one of their clinics), so
 * this one endpoint backs the admin center, clinic fax-failure dismissals, and
 * future patient surfaces alike.
 */
export async function resolveNotification(
  id: string
): Promise<{ success: true }> {
  return fetchWithAuth<{ success: true }>(
    `${API_BASE_URL}/notifications/${id}/resolve`,
    { method: 'POST' }
  );
}

export async function resolveSubjectNotifications(
  subjectType: SubjectType,
  subjectId: string
): Promise<{ success: true }> {
  return fetchWithAuth<{ success: true }>(
    `${API_BASE_URL}/notifications/resolve`,
    {
      method: 'POST',
      body: JSON.stringify({ subjectType, subjectId }),
    }
  );
}

/** Marks every open notification as seen by the requesting user (de-bolds the badge). */
export async function markNotificationsSeen(): Promise<{ success: true }> {
  return fetchWithAuth<{ success: true }>(
    `${API_BASE_URL}/notifications/seen`,
    { method: 'POST' }
  );
}

// ---- Project A: clinic join requests (signup match + join loop) ----

/**
 * The caller's join request, or null when they never matched (404). Serves
 * the /pending page and the post-join disposition prompt.
 */
export async function getMyJoinRequest(): Promise<ClinicJoinRequestView | null> {
  try {
    return await fetchWithAuth<ClinicJoinRequestView>(
      `${API_BASE_URL}/clinic-join-requests/mine`
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * "Ask to join" a chosen matched clinic (idempotent server-side). `candidateId`
 * is the opaque id from ClinicJoinRequestView.matches; omit it to accept the
 * server's default pin (single-match or legacy rows with no candidates).
 */
export async function requestToJoinMatchedClinic(
  candidateId?: string
): Promise<ClinicJoinRequestView> {
  return fetchWithAuth<ClinicJoinRequestView>(
    `${API_BASE_URL}/clinic-join-requests/mine/request`,
    {
      method: 'POST',
      body: JSON.stringify(candidateId ? { candidateId } : {}),
    }
  );
}

/**
 * "This is a different practice" / bail-out: mint a new clinic instead of
 * joining the match. From the waiting phase the join request stays open.
 */
export async function createNewClinicFromJoinRequest(
  clinicName: string
): Promise<ClinicJoinRequestView & { createdClinicId: string | null }> {
  return fetchWithAuth<
    ClinicJoinRequestView & { createdClinicId: string | null }
  >(`${API_BASE_URL}/clinic-join-requests/mine/create-new-clinic`, {
    method: 'POST',
    body: JSON.stringify({ clinicName }),
  });
}

/** The one-time post-join choice about the bailed-out solo clinic. */
export async function submitJoinRequestDisposition(
  choice: JoinRequestDispositionChoice
): Promise<ClinicJoinRequestView> {
  return fetchWithAuth<ClinicJoinRequestView>(
    `${API_BASE_URL}/clinic-join-requests/mine/disposition`,
    {
      method: 'POST',
      body: JSON.stringify({ choice }),
    }
  );
}

/** Open join-request notifications for the caller's clinics (the banner feed). */
export async function fetchIncomingJoinRequests(): Promise<{
  data: IncomingJoinRequest[];
}> {
  return fetchWithAuth<{ data: IncomingJoinRequest[] }>(
    `${API_BASE_URL}/clinic-join-requests/incoming`
  );
}
